import type { BidMember, BidRole, DirectoryPerson, FlattenCounts, Me, Profile, UserSettings, VersionDiff, VersionMeta, VersionTag } from './types';
import type { GridSnapshot, PartKind } from './grid';
import type { ArtefactType } from './artefact-type';
import type { SlidesTemplateLike } from '../slides/theme';
import type { SlideIconCategory, SlideIconSummary, SlideIconSvg } from '../slides/icons';
import type { ServerSlide } from '../slides/render-entry';
import { markTransient, reportError } from './errors';
import { isTransientStatus, reportTransientFailure } from './connectivity';
import { isAgentAvailable } from './agent-availability';
import { apiUrl, clearToken } from './backend';
import { freshToken, renewToken } from './session-token';
import { clientHeaders } from './desktop';
import { noteFloorHeaders } from './client-floor';
import type {
  BidRetentionCounts,
  BidRetentionView,
  RetentionPolicy,
  RetentionPreview,
  TenantRetentionView,
} from './retention';
import type { TemplateCatalogue } from './template-catalogue';

// Thin typed client over the OpenAPI REST API. In dev the Vite server proxies /v1
// directly to the backend (same-origin, empty base). In prod the SPA is served from a
// separate origin (Azure Static Web Apps), so the REST base is resolved at runtime from
// window.__NIOBE_CONFIG__ (public/config.js) or a build-time env var. The browser holds
// only a short-lived session token (stub identity in the MVP).

/**
 * The backend base and the session token now live in lib/backend.ts, so that modules THIS file
 * imports (agent-availability.ts, for the /agent/* backstop) can resolve a URL without an import
 * cycle. Re-exported here because every existing caller imports them from './api' — notably
 * lib/download.ts, which cannot go through request() (that is JSON-only) yet MUST resolve the
 * same base: a relative /v1 URL in prod never reaches the backend, it hits the Static Web App,
 * whose navigationFallback answers 200 with index.html, and the caller saves the app shell as a
 * .docx/.pptx.
 */
export { apiUrl, backendUrl, getToken, setToken, clearToken } from './backend';

/** One entry from the TEMPORARY `diagnosticGroupMembers` call — see its own doc comment below. */
export interface DiagnosticGroupMember {
  oid: string;
  displayName: string;
  upn: string | null;
  userType: string | null;
}

// ─── Corporate KB source catalogue (admin-only, /settings/kb-sources) ────────────────────────

/** One `kb_sources` row: what is indexed, from where, under what scope, and how it is cited. */
export interface KbSourceRecord {
  id: string
  name: string
  type: string
  siteUrl: string
  resourceId: string | null
  enabled: boolean
  /** False ⇒ citations are masked to `<displayAlias>/<file>` with no URL. Confidentiality, not
   *  access control — a restricted source's content is still retrieved and quoted. */
  accessible: boolean
  displayAlias: string | null
  /** Include globs. Empty = the whole library. */
  folders: string[]
  /** Exclude globs, subtracted from the includes. Exclude always wins. */
  excludeFolders: string[]
  updatedAt: string
}

/**
 * One person's Niobe usage, as read by Settings → User activity (`GET /v1/admin/user-activity`).
 * `desktop: null` means "never seen on Niobe Desktop" — which, until every install is past the
 * change that added the client headers, also covers a Desktop user on an older build. Inside it,
 * `version: null` is the same unknown, one level finer: Desktop calls were recorded, none of them
 * said which version.
 */
export interface UserActivity {
  requests7d: number
  requests30d: number
  lastSeenAt: string | null
  desktop: { version: string | null; lastSeenAt: string } | null
}

/** The fields a new source is created with (the server defaults everything omitted). */
export type NewKbSourceInput = Omit<KbSourceRecord, 'updatedAt'>

/** The editable fields. `id` is absent on purpose: it is the source_ref stamped on every chunk. */
export type KbSourcePatchInput = Partial<Omit<KbSourceRecord, 'id' | 'updatedAt'>>

/** An include pattern that names no folder of the library, and the path that would match (or null). */
export interface KbUnmatchedPattern {
  pattern: string
  suggestion: string | null
}

/**
 * What a save found when it checked the include patterns against the library's real folders, as
 * the admin. A warning riding on a SUCCESSFUL save, never a refusal: `skipped` = nothing to check,
 * `unavailable` = SharePoint could not be listed (whatever `unmatched` holds is still true).
 */
export interface KbScopeCheck {
  status: 'checked' | 'skipped' | 'unavailable'
  unmatched: KbUnmatchedPattern[]
}

/** A created or edited source. `scopeCheck` is absent from a backend that predates the check. */
export type KbSourceSaved = KbSourceRecord & { scopeCheck?: KbScopeCheck }

/** A durable indexation run, written by the pipeline wherever it runs (usually a shell script). */
export interface KbSyncRun {
  id: string
  sourceRef: string
  startedAt: string
  heartbeatAt: string
  finishedAt: string | null
  phase: 'crawling' | 'indexing' | 'done' | 'failed'
  /** Null while crawling — the crawl writes nothing else for 5-13 minutes. */
  totalFiles: number | null
  processed: number
  indexed: number
  failed: number
  skipped: number
  error: string | null
}

/** Per-source indexation state: the ledger rolled up, plus the latest run. */
export interface KbSourceSyncStatus {
  sourceRef: string
  lastSyncedAt: string | null
  /** When the source's last COMPLETED run ended — what the list shows as "Last indexed". A run that
   *  finished with some failed files counts; one that aborted does not. */
  lastIndexedAt: string | null
  hasDeltaToken: boolean
  files: { total: number; indexed: number; skipped: number; failed: number }
  chunks: number
  run: KbSyncRun | null
  /** The latest run's full crawl found nothing under the source's include/exclude patterns, so it
   *  held the drive back instead of sealing it as indexed. Absent from an older backend. */
  scopeMatchedNothing?: boolean
}

/** The corporate KB crawl lease (GET /v1/admin/kb-crawl/status): who the crawl runs as, and until
 *  when — never the token itself, which never reaches the browser. `lease: null` = paused. */
export interface KbCrawlStatus {
  lease: { holder: string; expiresAt: string } | null
  lastRun: KbSyncRun | null
}

/** A SharePoint document library, as the "+ New source" picker lists them. */
export interface KbSharepointDrive {
  id: string
  name: string
  webUrl: string
  isFolder: boolean
  isDrive: boolean
}

/**
 * Where a deck to import comes from (`POST /v1/slides/imports/staged`): the bytes of a file from
 * disk, or a SharePoint file the backend downloads as the member — by its ids or a pasted link.
 */
export type SlideImportSource =
  | { base64: string; name: string }
  | { sharepoint: { driveId: string; itemId: string } | { url: string } }

/** A vetted deck, readable for an hour by the member who staged it (backend routes/slide-imports.ts). */
export interface StagedDeck {
  stagedId: string
  name: string
  /** Every slide of the deck, `index` 1-based; a hidden one cannot be imported as a picture. */
  slides: Array<{ index: number; title: string; hidden: boolean }>
  size: number
  ratio: '16:9' | '4:3' | 'other'
  /** The deck's sensitivity label (`name` when the deck names it), `null` = none. */
  label: { id: string; name?: string } | null
  /** False: the deck does not carry the label the export stamps (Sec2) — the dialog says so. */
  labelIsSec2: boolean
  expiresAt: string
}

export type SlideImportMode = 'pictures' | 'template' | 'source'

/** Where "Keep source formatting" puts the deck's masters (AB#483): a new template (no name = after the deck), or merged into one. */
export type SlideImportDestination = { type: 'new'; name?: string } | { type: 'merge'; templateId: string }

/** What "Keep source formatting" did with the masters: `added` of them joined the template, `reused` of them it already held. */
export interface SlideImportTemplate {
  id: string
  name: string
  outcome: 'created' | 'reused' | 'merged'
  added: number
  reused: number
}

export type SlideImportStatus = 'rendering' | 'writing' | 'done' | 'locked' | 'failed'

/** What an element of the source became ("On a template of this bid"): `exact` ones are only counted. */
export type SlideImportOutcome = 'exact' | 'approximated' | 'cut_out' | 'dropped'

/** What the converter did with one slide (AB#443): how many elements came out each way, then each non-exact one. */
export interface SlideImportReport {
  counts: Record<SlideImportOutcome, number>
  entries: Array<{
    /** Its id on the source slide. */
    sourceShapeId: string
    name: string
    outcome: Exclude<SlideImportOutcome, 'exact'>
    /** Why, in the backend's words. */
    reason: string
    /** The element it became, which the report panel selects; absent for a dropped one. */
    elementId?: string
  }>
}

/** What a running import job is busy with. */
export type SlideImportStep = 'converting' | 'writing' | 'rendering' | 'cutouts'

/** How far a running job is: `done` of `total` in its step; `total` 0 = nothing to count at the moment. */
export interface SlideImportProgress {
  step: SlideImportStep
  done: number
  total: number
}

/** An import job (`GET /v1/slides/imports/:id`): the slides it wrote, in deck order, once done. */
export interface SlideImportJob {
  id: string
  room: string
  status: SlideImportStatus
  mode: SlideImportMode
  /** Each slide written, the source slide it shows, and — converted — its report. */
  slides: Array<{ slideId: string; sourceIndex: number; report?: SlideImportReport }>
  renders: 'pending' | 'ok' | 'failed'
  expiresAt: string
  error?: string
  /** Converted: cut-outs still placeholders, waiting for the original renders ("Retry renders"); absent when none. */
  cutOutsPending?: number
  /** A running job only; absent before its first report, and from an older backend. */
  progress?: SlideImportProgress
  /** A running job, once it reports: how far the whole job is, 0–100. The Jobs section shows the same number. */
  percent?: number
  /** A done import before its expiry: some original render is kept ("Compare with original" is offered). */
  rendersKept?: boolean
  /** "Keep source formatting", once a run got that far: the template its masters are in. */
  template?: SlideImportTemplate
}

/**
 * One import of a slides part (`GET /v1/slides/imports?room=`, AB#446): the job, its source deck,
 * and per slide whether its original render is still kept (2 days) — "Compare with original".
 */
export interface RoomSlideImport extends Omit<SlideImportJob, 'slides'> {
  sourceName: string
  createdAt: string
  /** Some original render is still kept. */
  rendersKept: boolean
  slides: Array<{ slideId: string; sourceIndex: number; report?: SlideImportReport; original: boolean }>
}

/** What the bid home's Jobs section lists (AB#462). A new kind adds a mapping (lib/bid-jobs.ts). */
export type BidJobKind = 'pptx_import' | 'indexing'
/** `planned` is in the contract for later: no kind produces it today. */
export type BidJobStatus = 'planned' | 'in_progress' | 'completed' | 'failed'
/** `renders_failed`: completed, with failed renders or cut-outs still waiting. `interrupted`: a run a member stopped. */
export type BidJobNote = 'renders_failed' | 'interrupted'

/** One job of a bid (`GET /v1/bids/:bid/jobs`, backend/domain/jobs.ts). */
export interface BidJob {
  id: string
  kind: BidJobKind
  /** What it works on: the deck's name, or the number of files once they are known. */
  subject: { name?: string; files?: number }
  /** `displayName` null = no name is known for them. */
  launchedBy: { userId: string; displayName: string | null }
  status: BidJobStatus
  /** A running job, once it can say how far it is: 0–100. */
  percent?: number
  note?: BidJobNote
  startedAt: string
  finishedAt?: string
  /** An import: the slides part it writes to. */
  room?: string
}

/** A failed `/v1` call, carrying enough for a caller to tell a RULE from a malfunction. */
export interface ApiError extends Error {
  status: number;
  /** The backend's error `code`, e.g. `last_admin` / `forbidden`. '' when it sent none. */
  code: string;
  /** The backend's own message, for showing verbatim. */
  serverMessage: string;
  /** The rest of the backend's error object (a 413's `stage` / `bytes` / `limit`, say). */
  details?: Record<string, unknown>;
}

/** Narrow an unknown catch value to {@link ApiError}. */
export function isApiError(e: unknown): e is ApiError {
  return e instanceof Error && typeof (e as ApiError).status === 'number';
}

/**
 * What a member action (a non-GET) says when the backend cannot be reached. One fixed message with
 * no per-endpoint detail, so repeated failures collapse into one dialog (lib/errors.ts drops a
 * duplicate that is still open) instead of stacking one per call.
 */
export const ACTION_UNREACHABLE =
  'Cannot reach the server, so your last action may not have gone through. Niobe is reconnecting — try again in a moment.';

async function request<T>(path: string, init?: RequestInit): Promise<T>
async function request<T>(path: string, init: RequestInit, opts: { allowNotFound: true }): Promise<T | null>
async function request<T>(path: string, init: RequestInit, opts: { quiet: true }): Promise<T>
async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: { allowNotFound?: boolean; quiet?: boolean } = {},
): Promise<T | null> {
  const method = init.method ?? 'GET';
  // A read is background data: polls, lists, the refetch on focus. Anything else is a member action.
  const read = method === 'GET' || method === 'HEAD';

  /**
   * The backend could not be reached: a fetch rejection, a gateway's 502/503/504, or the
   * passerelle's `backend_unreachable` (connectivity.ts#isTransientStatus). Always handed to the connectivity store, which shows "Reconnecting…",
   * pauses the polls and recovers in order (lib/connectivity.ts). A READ says nothing more: a
   * background poll has nobody to tell, and a dialog per endpoint is what used to stack up over the
   * editor after a wake. A member ACTION still says it failed, in one message, unless the caller
   * renders failures itself (`quiet`).
   */
  const unreachable = <E extends Error>(error: E): E => {
    reportTransientFailure();
    if (!read && !opts.quiet) reportError(ACTION_UNREACHABLE);
    return markTransient(error);
  };

  const send = async (token: string): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('Accept', 'application/json');
    // Which client is speaking, and at what version — see lib/desktop.ts#clientHeaders. Set here,
    // beside the Authorization the backend logs the call under, so every /v1 request carries it
    // and none can be forgotten one call site at a time.
    for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v);
    let res: Response;
    try {
      res = await fetch(apiUrl(`/v1${path}`), { ...init, headers });
    } catch (e) {
      throw unreachable(e instanceof Error ? e : new Error('network error'));
    }
    // The backend's client version floors ride on every /v1 response, a 401 or an error included
    // (lib/client-floor.ts). Read before anything below can throw.
    noteFloorHeaders(res.headers);
    return res;
  };

  const sent = await freshToken();
  let res = await send(sent);

  // ONE retry, and only behind a credential that actually CHANGED.
  //
  // Retrying is safe for every method here, not just the idempotent ones: the backend's auth check
  // is an `onRequest` hook (backend/inbound/http/api.ts), so a 401 is refused before any handler
  // runs and nothing can have been applied. But re-sending the SAME token is a guaranteed second
  // 401 — that is the dev/stub identity, and any renewal that failed and fell back to what we
  // already held.
  //
  // What used to happen instead was actively harmful — `clearToken()` then a rejection that
  // lib/errors.ts deliberately swallows. Nothing re-ran sign-in, so every later call went out with
  // no Authorization header at all and 401'd too, silently, until AuthGate's next 20-minute tick.
  if (res.status === 401) {
    const renewed = await renewToken();
    if (renewed && renewed !== sent) res = await send(renewed);
  }
  if (res.status === 401) {
    clearToken();
    throw new Error('unauthenticated'); // handled by the auth flow — not a user popup
  }
  // 404 with allowNotFound: silently return null (caller handles "not found" state).
  if (res.status === 404 && opts.allowNotFound) return null;
  if (!res.ok) {
    let detail = `${method} /v1${path} → ${res.status}`;
    let code = '';
    let message = '';
    // Did the BACKEND write this? Its error envelope says so. A gateway's 502/503/504 has none.
    let enveloped = false;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as { error?: string | { message?: string; code?: string } };
      const m = body?.error;
      enveloped = m !== undefined && m !== null;
      if (typeof m === 'string') {
        detail = m;
        message = m;
      } else if (m) {
        details = m as Record<string, unknown>;
        detail = m.message ?? detail;
        message = m.message ?? '';
        code = m.code ?? '';
      }
    } catch {
      /* non-JSON error body */
    }
    const error = Object.assign(new Error(`API ${res.status} on ${path}`), {
      status: res.status,
      code,
      // The backend's own wording, for a caller that wants to show it verbatim.
      serverMessage: message,
      ...(details && { details }),
    });
    if (isTransientStatus(res.status, code, enveloped)) throw unreachable(error);
    // `quiet` is for refusals the CALLER renders itself — a rule like the last-admin guard is not
    // a malfunction, and popping the global error dialog over it says the wrong thing.
    if (!opts.quiet) reportError(`The server returned an error (${res.status}).`, detail);
    throw error;
  }
  return (await res.json()) as T;
}

export interface PartItem {
  partId: string;
  /** The immutable room key — used to open/edit the item. */
  name: string;
  title: string;
  /** What the room CONTAINS: prose in a Y.Text, or a structured grid. Fixed at creation — the
   *  Canvas branches its editor on it. Absent on an older server = 'markdown'. */
  kind?: PartKind;
  /**
   * The artefact-wide § range the part spans ("3", or "3-5" when it holds several top-level §),
   * `null` when it holds no heading. Derived server-side on every read, so it re-evaluates on its
   * own when parts are reordered. This is the ONLY number the explorer and the dashboard show —
   * a positional index next to it would just duplicate it. Absent on the synthetic workflow
   * artefacts, which own no §.
   *
   * Always null for a GRID part: its markdown projection carries no heading by construction.
   */
  section?: string | null;
  /** The part is READ-ONLY (AB#209): readable and exportable, never edited — by anyone. The server
   *  refuses every content write (REST 423 + a read-only CRDT connection); the editor opens it as
   *  it does for a viewer. Absent = editable. */
  readOnly?: boolean
  /** A read-only copy's provenance, for the editor's banner: the source part's room key and the
   *  instant it was restored from (ISO 8601). */
  restoredFrom?: { room: string; at: string }
}
/** GET /v1/artefact-templates — the template library's categories (its folders) and templates (its artefacts). */
export type ArtefactTemplateCatalogue = TemplateCatalogue;

export interface ArtefactOutline {
  bidId: string;
  /** The immutable artefact id (room key segment). */
  artefactId: string;
  title: string;
  /** The explorer folder holding it, or null for the root level. Navigation only. */
  folderId: string | null;
  /**
   * prose (markdown + grid parts, Word/web/Markdown/Excel) or slides (slides parts, PowerPoint) —
   * chosen at creation, immutable (lib/artefact-type.ts). Absent on an older server and on the
   * synthetic workflow artefacts: read it through `artefactTypeOf`, which makes that prose.
   */
  type?: ArtefactType;
  parts: PartItem[];
  /** A deck's own default PowerPoint template and master (AB#441); null/absent = the bid default. */
  slidesTemplate?: ArtefactSlidesTemplate | null;
  /** A prose artefact's own Word template id (AB#441); null/absent = the bid default. */
  wordTemplate?: string | null;
}

/** A deck's default bid Office template (PowerPoint) and the master it renders on. */
export interface ArtefactSlidesTemplate {
  templateId: string;
  master: number;
}

/**
 * A virtual folder of the artefact explorer (left panel). Server-persisted, so the grouping is
 * shared by the bid's members — but it is navigation metadata only: room keys, the CRDT and the
 * export order never see it.
 */
export interface Folder {
  bidId: string;
  /** Immutable id; renaming changes the title only. */
  folderId: string;
  title: string;
  /** null = a root-level folder. Nesting is capped at 5 levels (server-enforced). */
  parentFolderId: string | null;
  position: number;
}

export interface Bid {
  id: string;
  name: string;
  /**
   * The bid's roster, for the home-page filter to search member names without a per-bid round
   * trip (GET /v1/bids now rides it along — services.ts#listMyBids). Optional because older test
   * fixtures still build a bare `{ id, name }`; treat a missing array the same as an empty one.
   */
  members?: BidMember[];
  /**
   * The client (backend migration 016), optional — `null` or absent when not filled in. There is no
   * opportunity field: `name` IS the opportunity's name. Never build a label from these by hand:
   * {@link bidDisplayLabel} is the one rule for the bid's label.
   */
  client?: string | null;
  /** The CRM reference, canonical `OP# 12345678` (lib/thor.ts). Reference only. */
  thorId?: string | null;
  /**
   * ISO time of the bid's last content save by anyone, or its creation when nothing has been saved
   * yet (GET /v1/bids — services.ts#listMyBids). The home page sorts by it, most recent first.
   * Optional for the same reason as `members`.
   */
  lastModified?: string;
}

/**
 * The bid's label wherever it is named (breadcrumb, Dashboard, Bid Settings, Workflow header):
 * "Client - Bid name" when a client is set, else the bid name. Never "null", "undefined" or a
 * dangling " - ": a blank client counts as absent.
 */
export function bidDisplayLabel(bid: Pick<Bid, 'name' | 'client'>): string {
  const client = bid.client?.trim();
  return client ? `${client} - ${bid.name}` : bid.name;
}

/** The editable identity fields. On a create, blank ones may be omitted; on a patch an absent field
 *  is left as it is and `''` clears it. */
export interface BidMetaFields {
  client?: string;
  thorId?: string;
}

/** What Bid Settings may change on an existing bid: its identity fields, plus its display `name` —
 *  never blank (never sent as `''`), and a rename never changes the bid's id (its URL). */
export type BidPatchFields = BidMetaFields & { name?: string };

/**
 * A Thor (CRM) lookup, as the member is told about it. `not_found`: Thor answered and has no such
 * opportunity (check the number). `unavailable`: Thor could not be asked at all (fill the fields in
 * by hand). Neither blocks anything. `opportunity` is Thor's `[Opportunity Name]`: a form puts it
 * into the BID NAME (the bid name is the opportunity name — a bid has no opportunity field).
 */
export type ThorLookupResult =
  | { status: 'found'; thorId: string; client: string | null; opportunity: string | null }
  | { status: 'not_found' }
  | { status: 'unavailable' };

/**
 * Where a bid's source material lives in SharePoint (mirror of backend/domain/bid-site.ts).
 *
 * A POINTER, nothing else: the Graph ids needed to re-open the location and the names needed to say
 * where it is. The documents never come near Niobe — the backend reads the folder ON BEHALF OF the
 * member (see `spSites`/`spDrives`/`spChildren` below), so SharePoint's per-user ACL is still what
 * decides who sees what, and this pointer grants nobody anything.
 *
 * PARTIAL by depth: a pin may stop at the SITE (no `driveId`/`driveName`/`itemId` — the pane then
 * lists the site's libraries), at a LIBRARY (`itemId: 'root'`, `folderPath: ''`) or at a FOLDER.
 * When `driveId` is present, `itemId` is present too.
 */
export interface BidSite {
  siteId: string;
  siteName: string;
  siteUrl: string;
  /** Absent on a site-only pin. */
  driveId?: string;
  driveName?: string;
  /** Drive-item id of the folder, or 'root' when the library itself is the location. Absent on a
   *  site-only pin. */
  itemId?: string;
  /** Human path under the library ('General/RFP'); '' when the library root (or the site) IS the
   *  location. */
  folderPath: string;
  /** The pinned location's SharePoint address — what the dashboard row opens in a new tab. */
  webUrl: string;
}

/**
 * Any SharePoint address (a sharing link, a library or folder URL, a file URL, a bare site URL)
 * resolved by the backend AS THE CALLER, to the deepest level it identifies. A file link arrives
 * already resolved to its parent folder. Mirrors the backend's `/sharepoint/resolve` answer.
 */
export interface SpResolved {
  site: { id: string; displayName: string; webUrl: string };
  /** Present from the library level down. */
  drive?: { id: string; name: string; webUrl: string };
  /** Present at the folder level only. */
  item?: { id: string; name: string; webUrl: string };
  /** Path under the library; '' (or absent) at the library root. */
  folderPath?: string;
}

/**
 * A SharePoint site the member can reach, as the backend's browse proxy reports it.
 *
 * "The member can reach" is literal and is the whole design: the backend exchanges the caller's own
 * Niobe token for a Graph one on their behalf, so Graph applies SharePoint's per-user ACL to this
 * search exactly as it did when the browser held a Graph token itself.
 */
export interface SpSite {
  id: string;
  displayName: string;
  webUrl: string;
}

/** One row of the bid-site browser: a document library, a folder inside one, or a file. */
export interface SpEntry {
  /** Drive id when `isDrive`, otherwise the drive-item id. */
  id: string;
  name: string;
  webUrl: string;
  isFolder: boolean;
  /** A document library (a Graph *drive*), i.e. the top level of a site — not a folder in one. */
  isDrive: boolean;
  /** Whether this FILE is already in the bid's own KB. Always absent for a folder or a library —
   *  see backend/domain/sharepoint-browse.ts#SpEntry for why a folder rollup is not computed here. */
  indexed?: boolean;
}

/**
 * One item the member checked in the bid-site browser to index — a single file, or a folder whose
 * contents (recursively) should all go into the bid's own KB. Mirrors
 * backend/app/services.ts#SpIndexSelection.
 */
export interface SpIndexSelection {
  itemId: string;
  name: string;
  isFolder: boolean;
}

/** Outcome of one click-to-index run — counts only. Mirrors backend/app/services.ts#SpIndexResult. */
export interface SpIndexResult {
  ingested: number;
  skipped: number;
  failed: number;
  /** Selected files never attempted because a run-wide guardrail stopped the run first. */
  notAttempted: number;
  /** Which guardrail stopped the run short of the whole selection, if any did. */
  stopped?: 'chunk_cap' | 'file_limit' | 'cancelled';
}

/** One file's outcome, appended live as a run processes it. Mirrors
 *  backend/app/services.ts#SpIndexProgressEntry. */
export interface SpIndexProgressEntry {
  name: string;
  outcome: 'ingested' | 'skipped' | 'failed';
  /** Why — a skip's format/size/duplicate reason, or a failure's error text. Absent for
   *  'ingested'. Shown in the "Indexation result" modal. */
  reason?: string;
}

/** A click-to-index background job, polled by id. Mirrors backend/app/services.ts#SpIndexJob. */
export interface SpIndexJob {
  id: string;
  status: 'running' | 'done' | 'error';
  startedAt: string;
  finishedAt?: string;
  result?: SpIndexResult;
  error?: string;
  /** Grows live while the job runs — the browser reads `progress.length / totalFiles` for a
   *  percentage instead of only the finished summary. */
  progress: SpIndexProgressEntry[];
  totalFiles?: number;
  /** Set once "Interrupt indexation" has been clicked — the run stops before its next file. */
  cancelRequested?: boolean;
}

/** Office brand colours (hex WITHOUT '#') + optional logo, for the generated PowerPoint. */
export interface Brand {
  primary: string;
  accent: string;
  ink: string;
  logoDataUri?: string;
}
/** Why a Word template cannot serve the export — the backend's refusal `code` on upload. */
export type WordTemplateProblem = 'not_word' | 'macro_enabled' | 'no_content_marker';

/** Why a slides template is refused on upload (422) — the backend's refusal `code`. */
export type SlidesTemplateProblem = 'not_pptx' | 'macro_enabled' | 'too_large';

/** A template file refused for its size (413 `too_large`): as sent, once trimmed, or unpacked. */
export interface TemplateSize {
  stage: 'upload' | 'kept' | 'expanded';
  bytes: number;
  limit: number;
}
/** What the server trimmed off an uploaded PowerPoint template (its sample slides). */
export interface TemplateTrim {
  uploadedBytes: number;
  keptBytes: number;
  removedSlides: number;
}

/** The theme colour slots of a PowerPoint template. */
export type ThemeColorSlot =
  | 'dk1' | 'lt1' | 'dk2' | 'lt2'
  | 'accent1' | 'accent2' | 'accent3' | 'accent4' | 'accent5' | 'accent6'
  | 'hlink' | 'folHlink';

/** A PowerPoint template parsed at upload (mirror of backend/domain/slide-theme.ts#SlideThemeSummary). */
export interface SlideThemeSummary {
  /** Slide size in EMU. */
  slideSize: { cx: number; cy: number };
  /** Index of the slide master decks render on. */
  master: number;
  hasNotesMaster: boolean;
  masters: Array<{
    name: string;
    colors: Partial<Record<ThemeColorSlot, string>>;
    fonts: { major: string; minor: string };
    /** Display names (localised, e.g. "Titre seul") + the declared layout type. */
    layouts: Array<{ name: string; type: string }>;
  }>;
}

/** A template-set scope; components resolve bid → corporate (most specific wins). */
export type TemplateScope = 'corporate' | 'bid';
/** Where a component's EFFECTIVE value comes from; `null` = nowhere (no template at all). */
export type TemplateSource = TemplateScope | null;
/** The three independently resolved components of a template set. */
export type TemplateComponent = 'word' | 'slides' | 'brand';

/** One scope's template set as `GET /v1/template-sets` describes it. */
export interface TemplateSetView {
  scope: TemplateScope;
  /** The bid id (bid scope), `null` (corporate). */
  owner: string | null;
  /** May the caller edit THIS scope. */
  canEdit: boolean;
  /** `problem`: why this scope's OWN stored template is not used by the export. */
  word: { own: boolean; problem: WordTemplateProblem | null; source: TemplateSource };
  /**
   * `theme`: the EFFECTIVE PowerPoint template's parsed palette, fonts and layouts. `corporate`:
   * the catalogue entry it is (null when the bid's own upload wins, or nothing resolves) — `chosen`
   * false = the bid follows the corporate default. `master`: the master it renders on.
   * (`corporate` / `master` are absent from a server older than AB#220.)
   */
  slides: {
    own: boolean;
    source: TemplateSource;
    theme: SlideThemeSummary | null;
    corporate?: CorporateTemplateRef | null;
    master?: number | null;
  };
  /** `derived`: no scope sets colours, so they come from the PowerPoint template's theme. */
  brand: { own: Brand | null; effective: Brand; source: TemplateSource; derived: boolean };
}

/** A kind of corporate template in the catalogue — PowerPoint only today. */
export type CorporateTemplateKind = 'slides';

/** The catalogue entry a bid's effective PowerPoint template is (TemplateSetView.slides.corporate). */
export interface CorporateTemplateRef {
  id: string;
  name: string;
  builtin: boolean;
  /** It is the current corporate default. */
  default: boolean;
  /** The bid picked it (false = it follows the corporate default). */
  chosen: boolean;
}

/**
 * One entry of the corporate template catalogue (`GET /v1/corporate-templates`, AB#220): built-in
 * (shipped, read-only) or uploaded by a tenant admin; `default` = the one every bid that never chose
 * renders on; `retired` = withdrawn (listed only with `includeRetired`), never deleted.
 */
export interface CorporateTemplate {
  id: string;
  kind: CorporateTemplateKind;
  name: string;
  builtin: boolean;
  default: boolean;
  retired: boolean;
  /** `theme.master` = the slide master the entry renders on. */
  theme: SlideThemeSummary;
}

/**
 * A kind of BID OFFICE TEMPLATE (backend/domain/office-templates.ts): `slides` = a PowerPoint
 * template, `word` = a Word template. Not the artefact template library (lib/template-library.ts).
 */
export type OfficeTemplateKind = 'slides' | 'word';

/**
 * Where a bid Office template came from: a snapshot of a corporate catalogue entry, an upload, the
 * template the bid used before its library existed (`legacy`), or a deck imported keeping its source formatting.
 */
export interface OfficeTemplateOrigin {
  type: 'catalogue' | 'upload' | 'legacy' | 'imported';
  catalogueId?: string;
  master?: number;
  source?: 'bid' | 'corporate';
  chosen?: boolean;
}

/** One of a bid's Office templates, as `GET /v1/bids/:bid/office-templates` lists it (AB#441). */
export interface OfficeTemplate {
  id: string;
  kind: OfficeTemplateKind;
  name: string;
  origin: OfficeTemplateOrigin;
  /** The flag of the former Imported slot: new templates carry false, and one that has it is a template like any other. */
  imported: boolean;
  /** The current version (1 at creation). */
  version: number;
  /** The bid default of its kind: what an artefact without a template of its own uses. */
  default: boolean;
  /**
   * The artefacts it renders: those that name it, for the default those that name none, and (AB#445)
   * the decks whose slides name it. `slides` (PowerPoint): how many of that artefact's slides are
   * drawn on it; absent on an older server.
   */
  usedBy: Array<{ artefactId: string; title: string; slides?: number }>;
  /** PowerPoint (AB#445): how many slides, across the bid, are drawn on it; absent on an older server. */
  slides?: number;
  /** PowerPoint (AB#454): per layout id, how many slides of the bid are drawn on it (absent = none). */
  layoutSlides?: Record<string, number>;
  /** A PowerPoint template's palette, fonts and layouts per master (null for Word). */
  theme: SlideThemeSummary | null;
  /**
   * Its shared draft (AB#453): the room, the unpublished theme writes and their authors, and the
   * settings that differ from the published version (pending = `fields` not empty). Absent on an
   * older server.
   */
  draft?: { room: string; unpublished: number; by: DraftAuthor[]; fields: string[]; layouts?: string[] };
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** A (PowerPoint template, master) pair of a bid's library: where a deck or a slide is drawn (AB#445). */
export interface DeckTemplateRef {
  templateId: string;
  /** The slide master, by index in the template's package. */
  master: number;
}

/** One PowerPoint template of the bid's library, as a deck's directory lists it (AB#445). */
export interface DeckTemplate {
  id: string;
  name: string;
  /** The bid default template. */
  default: boolean;
  /** Its current version: part of its views' cache keys, so a new version redraws. */
  version: number;
  /** Its own master: where a slide moved onto it (not the deck default) lands. */
  master: number;
  /** Every master, in order (index = master), with its layouts in the master's order. */
  masters: Array<{ name: string; layouts: Array<{ id: string; name: string; type: string }> }>;
}

/**
 * A deck's PowerPoint templates (`GET /v1/slides/templates?room=`, AB#445): its default pair and
 * the bid's library. A slide names its template (absent = the deck default) and a layout of it;
 * slides/deck-templates.ts#slideTemplateRef resolves the pair it is drawn on.
 */
export interface DeckTemplates {
  room: string;
  /**
   * The deck default: the artefact's own template and master, else the bid default and its master.
   * null = no PowerPoint template resolves (the default theme and the built-in layouts).
   */
  deck: DeckTemplateRef | null;
  /** The bid's PowerPoint templates, oldest first. */
  templates: DeckTemplate[];
}

/** One kept version of a bid Office template (the last 10). */
export interface OfficeTemplateVersion {
  version: number;
  contentHash: string;
  createdBy: string;
  /** Made by the agent of `createdBy` (a publish, AB#453); absent = the member. */
  createdByKind?: 'agent';
  createdAt: string;
}

/** Who made unpublished changes in a template's draft: a member, or a member's agent (AB#453). */
export interface DraftAuthor {
  id: string;
  name: string;
  kind: 'human' | 'agent';
}

/** A setting's value in a template's draft (backend domain/office-template-draft.ts#ThemeFieldValue). */
export type ThemeFieldValue =
  | string
  | number
  | boolean
  | null
  | { type: 'solid'; color: string }
  | { type: 'picture'; blobId: string }
  | { type: 'picture'; media: string }
  | { type: 'master' }
  | { type: 'other' };

/** What a template offers to its settings: masters with their layouts, its pictures, Word's style ids. */
export interface TemplateDraftStructure {
  /** PowerPoint: the slide size in EMU (AB#476); a draft seeded before holds none. */
  slideSize?: { cx: number; cy: number };
  masters?: Array<{ id: string; name: string; layouts: Array<{ id: string; name: string; type: string }> }>;
  pictures: Array<{ field: string; media: string; name: string; parts: string[] }>;
  styles?: { heading1: string; heading2: string; heading3: string; body: string };
}

/** A template's shared draft (`GET /v1/bids/:bid/office-templates/:id/draft`, AB#453). */
export interface TemplateDraftView {
  templateId: string;
  kind: OfficeTemplateKind;
  name: string;
  /** Its room, `<bid>/_tpl-<templateId>/draft`: co-edited live over the WebSocket. */
  room: string;
  /** The template's latest (published) version. */
  version: number;
  /** The version the draft is based on. */
  baseVersion: number;
  fields: Record<string, ThemeFieldValue>;
  base: Record<string, ThemeFieldValue>;
  structure: TemplateDraftStructure;
  unpublished: { count: number; by: DraftAuthor[]; fields: string[]; layouts?: string[] };
  /** May the caller write the draft, publish and discard (canEditBid)? */
  canEdit: boolean;
}

/** What `POST …/office-templates/:id/publish` answers. */
export interface PublishResult {
  /** false = nothing to publish: no version was made. */
  changed: boolean;
  version: number;
  fields: string[];
  skipped: string[];
  template: OfficeTemplate;
  /** A PowerPoint template: the slides of the bid drawn on it. */
  slides: number;
  /** The artefacts it renders. */
  artefacts: number;
  /** A PowerPoint template: the masters and layouts it wrote, by name (AB#457). */
  layouts?: string[];
}

/** One slide of a deck as `GET /v1/slides?room=` lists it (AB#457's Preview finds the slides on a layout). */
export interface SlideListing {
  id: string;
  index: number;
  layout?: string;
  template?: string;
  title: string;
  elements: number;
}

// ─── Workflow types (mirror of backend/domain/workflow.ts) ──────────────────

// ─── User skill types ────────────────────────────────────────────────────────

export interface UserSkill {
  id: string;
  label: string;
  description: string;
}

/**
 * A skill Claude Code discovered on the member's own machine — mirrors `GET /agent/local-skills`.
 * Claude-only: `source` distinguishes a personal (`~/.claude/skills/`) skill from a project
 * (`.claude/skills/`) one, and `file` is the absolute path to the `SKILL.md` that defines it, so
 * the member can see exactly where each skill comes from.
 */
export interface LocalSkill {
  name: string;
  description: string;
  source: 'personal' | 'project';
  file: string;
}

export type NodeStatut = 'a_faire' | 'en_cours' | 'a_revoir' | 'valide' | 'bloque';
export type StatusGlobal = 'a_qualifier' | 'en_cours' | 'soumis' | 'gagne' | 'perdu';
export type WorkflowType = 'ao-standard' | 'ao-public' | 'ao-prive';

export interface WorkflowMeta {
  titre: string;
  client: string;
  date_reception?: string;
  date_limite_depot?: string;
  chef_de_projet_offre?: string;
  valeur_estimee_eur?: number;
}

export interface WorkflowNode {
  nodeId: string;
  section: string;
  statut: NodeStatut;
  dependsOn: string[];
  agent?: string;
  skill?: string;
  owner?: string;
  surCheminCritique: boolean;
  enabled: boolean;
  notes?: string;
  artifactRoom?: string;
  reviewRoom?: string;
  /** Hidden from the nav (working-docs); invocable as slash command only. */
  navHidden?: boolean;
  ordinal: number;
}

export interface BidWorkflow {
  bidId: string;
  workflowType: WorkflowType;
  statusGlobal: StatusGlobal;
  meta: WorkflowMeta;
  nodes: WorkflowNode[];
}

/**
 * One entry of the static skill catalog — mirrors `WorkflowSkillCatalogEntry` in
 * backend/domain/workflow.ts. Enough to power the chat's slash-menu before a workflow has been
 * created for a bid (`GET /v1/workflow` answers 200 with a `null` body at that point).
 */
export interface WorkflowCatalogNode {
  nodeId: string;
  section: string;
  skill?: string;
  navHidden?: boolean;
  enabled: boolean;
}

export interface WorkingDocNode {
  nodeId: string;
  section: string;
  artifactRoom: string;
  reviewRoom: string;
}

/**
 * Calls the local-agent endpoint (NOT under /v1 — the gateway's own /agent/* surface).
 *
 * MVP 1 has no gateway: the AI capability gate is OFF, so this throws loudly before touching
 * the network. Every AI affordance in the UI is hidden behind the same gate, so this is a
 * backstop for stray calls — it must never be reached in normal MVP-1 flow. (MVP 2 re-enables
 * the gate and this call path.)
 */
async function agentRequest<T>(path: string, body: unknown): Promise<T> {
  if (!isAgentAvailable()) throw new Error('AI channel unavailable (no gateway)');
  const token = await freshToken();
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v);
  const res = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      /* non-JSON body */
    }
    // `status` rides along the same way `request()` attaches it to a /v1 failure: the sign-in
    // code route answers 400 / 404 / 409 with three genuinely different meanings, and branching on
    // the status is the alternative to parsing the sentence.
    throw Object.assign(new Error(detail || `agent ${res.status} on ${path}`), { status: res.status });
  }
  return (await res.json()) as T;
}

/** GET against the local-agent surface (NOT under /v1 — e.g. /agent/jobs). Gated like agentRequest. */
async function agentGet<T>(path: string): Promise<T> {
  if (!isAgentAvailable()) throw new Error('AI channel unavailable (no gateway)');
  const token = await freshToken();
  const headers = new Headers({ Accept: 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v);
  const res = await fetch(path, { headers });
  if (!res.ok) throw new Error(`agent ${res.status} on ${path}`);
  return (await res.json()) as T;
}

export interface AskResult {
  citations: { sourceId: string; sourceName: string }[];
  chunks: { sourceId: string; sourceName: string; content: string }[];
}

/**
 * An indexed source in a bid's knowledge base — the shape returned by `GET /v1/sources`
 * (mirror of the backend's `sourceSchema`). Nullable fields are honestly `null` when the
 * backend has no value (e.g. a source ingested without a recorded uploader/date).
 */
export interface KbSource {
  id: string;
  name: string;
  mime: string | null;
  category: string | null;
  chunks: number;
  uploadedBy: string | null;
  uploadedAt: string | null;
}

/** Which local agent CLI, as detected on the member's machine — mirrors the passerelle's `GET /agent/status` (Settings → AI agent). */
export type AgentId = 'claude' | 'copilot' | 'opencode';

/** One detected CLI: its absolute path, whether it's the real binary or a shim, and how it was
 *  found. `authenticated`/`account` are reported for Claude and GitHub Copilot — installed does
 *  not imply signed in, and a headless chat call against an unauthenticated CLI fails every time. */
export interface AgentStatusResponse {
  agents: Partial<
    Record<
      AgentId,
      {
        path: string;
        kind: 'native' | 'shim' | 'unknown';
        /** `claude-desktop`: the CLI copy the Claude Desktop app manages for itself (Claude only). */
        via: 'override' | 'path' | 'known-dir' | 'claude-desktop';
        target?: string;
        /**
         * Claude and Copilot. Read differently per agent:
         *
         * - **Claude** — always sent once detected; only a strict `true` counts as signed in.
         * - **Copilot** — TRI-STATE. Absent means "not known yet": the passerelle reads it in the
         *   background (the Copilot runtime cold-starts in ~14 s) and omits it until that read is
         *   in. Only a strict `false` means signed out — an unknown value must never hide a
         *   Copilot that works.
         */
        authenticated?: boolean;
        /** The signed-in account: Claude's email, Copilot's GitHub login. */
        account?: string;
        /**
         * Claude only. The SELECTED CLI's own `--version` line, e.g. `2.1.263 (Claude Code)` — the
         * passerelle runs the newest install it found, and `path` above is that one.
         */
        version?: string;
        /** Claude only. Every Claude Code found on the machine, with its version when readable. */
        candidates?: { path: string; source: 'override' | 'path' | 'known-dir' | 'claude-desktop'; version?: string }[];
        /**
         * Claude only. Does the CLI meet Niobe's version floor?
         *
         * **`undefined` means UNKNOWN and must render nothing at all** — an older passerelle that
         * does not send the field, an unreadable `--version`, or a Copilot/OpenCode-only machine.
         * Only a strict `false` is evidence of an outdated CLI. And `false` is still only a
         * warning: an outdated CLI works, so this never gates the chat.
         */
        versionOk?: boolean;
        /** Claude only. The floor itself, so the UI never hardcodes a version string. */
        minVersion?: string;
        /**
         * Claude only, epoch ms. The HARD login expiry (`refreshTokenExpiresAt`): refreshing never
         * moves it, so this is the date after which only a new sign-in works.
         *
         * Absent when the passerelle could not read it (macOS Keychain, no login at all) **and
         * whenever `tokenMode` is `'long-lived'`** — the `/login` credential is still on disk then,
         * but nothing is using it, so warning about its expiry would contradict the panel right
         * above and offer a Renew button that cannot change what authenticates.
         */
        loginExpiresAt?: number;
        /** Claude only, epoch ms. The access token's expiry — moves on every refresh. Diagnostic.
         *  Dropped alongside `loginExpiresAt` while a long-lived token is in use. */
        accessExpiresAt?: number;
        /**
         * Claude only. What authenticates RIGHT NOW — not what is stored.
         *
         * `'long-lived'` only while the member's opt-in `claude setup-token` token is present AND
         * not rejected; a stored-but-rejected token reports `'login'` with `tokenRejected: true`,
         * because injection has stopped and their ordinary sign-in is what answers again.
         * `undefined` means an older passerelle — treat it exactly like `'login'`.
         */
        tokenMode?: 'login' | 'long-lived';
        /** Claude only, epoch ms. When the long-lived token was saved. Present only with `'long-lived'`. */
        tokenSavedAt?: number;
        /** Claude only, epoch ms, `tokenSavedAt` + 365 d. An ESTIMATE — the CLI never prints the
         *  real expiry, so every rendering of it says "around" (DEBT.md). */
        tokenExpiresAt?: number;
        /** Claude only. A run was refused while the long-lived token was what authenticated, so
         *  Niobe stopped injecting it. In-session only: after a relaunch the shell's own store is
         *  what remembers, and the Settings panel reads that rather than this. */
        tokenRejected?: boolean;
      }
    >
  >;
  missing: AgentId[];
  available: boolean;
  /**
   * Per installable agent, the exact command the passerelle's assisted install would run on the
   * member's own machine — Windows PowerShell, macOS/Linux shell, whichever applies.
   *
   * The UI must NEVER hard-code an install command: it did once, and the string it showed was a
   * Windows CMD one-liner displayed on every OS, missing the `.\` prefix the passerelle actually
   * uses. Absent from an older passerelle, and the UI then simply shows no command at all.
   */
  installCommands?: Partial<Record<AgentId, string>>;
}

/** Outcome of an assisted install or update — the `result` of an `'install-agent'`/`'update-agent'`
 *  job (GET /agent/jobs/:id). */
export interface InstallResult {
  ok: boolean;
  version?: string;
  /** Whether the installed CLI now meets the floor. `undefined` = the version could not be read —
   *  the update then reports plain success rather than claiming either way. */
  versionOk?: boolean;
  error?: string;
  log: string;
}

/** Answer to `POST /agent/warmup`. `ran:false` carries the gate's reason, for diagnostics only —
 *  the UI never shows it. `jobId` is present exactly when `ran` is true. */
export interface WarmupResponse {
  ran: boolean;
  reason?: string;
  jobId?: string;
}

/** Outcome of an assisted sign-in — the `result` of a `'login-agent'` job (GET /agent/jobs/:id). */
export interface LoginResult {
  ok: boolean;
  /** Claude: the account's email. */
  email?: string;
  /** Copilot: the GitHub login it signed in as. */
  account?: string;
  error?: string;
  log: string;
}

export type AgentJobKind = 'edit' | 'ask' | 'improve' | 'chat' | 'svg' | 'install-agent' | 'login-agent' | 'update-agent' | 'warmup-agent';
export type AgentJobStatus = 'running' | 'done' | 'error' | 'cancelled';

/**
 * A machine-readable cause on a failed job (`AgentJob.errorCode`), for the cases the UI must react
 * to rather than merely display: the member's local Claude Code login is dead and every AI path
 * will keep failing until they sign in again (`agent_auth_required`), or the API refused the model
 * because the CLI is too old (`agent_update_required`, with `requiredVersion`/`currentVersion`).
 *
 * A TYPE, not a runtime constant — `lib/api` is mocked wholesale by the component tests, and a
 * runtime export read through that mock throws "No export is defined on the mock". Compare against
 * the literal (`'agent_auth_required'`, `'agent_update_required'`).
 */
export type AgentErrorCode = 'agent_auth_required' | 'agent_update_required';

/** A live tool-call step streamed into the working bubble while the agent runs. */
export interface AgentProgressEvent {
  at: string;
  kind: 'tool';
  tool: string;
  target?: string;
  status: 'start' | 'done';
}

/** The launcher's answers to a lock question (docs/spec/30-coediting.md "Lock conflicts"). */
export type AgentLockChoice = 'ignore' | 'retry' | 'force'

/** One target a run skipped because it stayed locked — what the ask card lists. */
export interface AgentLockTarget {
  key: string
  /** "§3 of ACME/proposal/intro", "slide s2 (t1) of ACME/pitch/deck"… */
  label: string
  room: string
  /** human = another member is editing it; ai = another agent's live claim. */
  kind: 'human' | 'ai'
  holder: { id: string; name: string }
}

/** A paused run's question to the member who launched it (`AgentJob.ask`, passerelle jobs.ts). */
export interface AgentLockAsk {
  /** Absent on a lock question; `'scope'` is {@link AgentScopeAsk}. */
  kind?: 'lock'
  id: string
  targets: AgentLockTarget[]
  /** `force` only when a member (not an agent) holds a target. */
  choices: AgentLockChoice[]
  /** ISO: the passerelle answers `ignore` itself past this. */
  expiresAt: string
}

/** The member's answers to a scope question (AB#337): widen the chat target, or stay on it. */
export type AgentScopeChoice = 'widen' | 'stay'

/**
 * A paused run's other question, on the same channel (`AgentJob.ask` with `kind: 'scope'`): the
 * request needs more than the chat target, so the agent asks to widen it (part < artefact < bid).
 */
export interface AgentScopeAsk {
  kind: 'scope'
  id: string
  /** The current chat target. */
  from: ChatScope
  /** The wider target the agent asks for. */
  to: ChatScope
  /** Why, in the agent's words. */
  reason: string
  choices: AgentScopeChoice[]
  /** ISO: the passerelle answers `stay` itself past this. */
  expiresAt: string
}

/** One thing a run left undone because of a lock (`AgentChatResult.undone`). */
export interface AgentUndoneTarget {
  label: string
  room: string
  kind: 'human' | 'ai'
  /** The holder's display name. */
  holder: string
  /** ignored = the member chose it; no_answer = the question timed out; still_locked = never settled. */
  reason: 'ignored' | 'no_answer' | 'still_locked'
}

/** A background agentic job tracked by the passerelle (survives a browser refresh). */
export interface AgentJob {
  id: string;
  kind: AgentJobKind;
  status: AgentJobStatus;
  room?: string;
  bid?: string;
  /** Which CLI an `'install-agent'`/`'login-agent'` job targets. Absent for every other job kind. */
  agent?: AgentId;
  prompt?: string;
  /** Live tool-call progress (populated while running, kept after done). */
  progress?: AgentProgressEvent[];
  /** The answer text written so far, while running (Claude agent only; capped by the passerelle).
   *  Gone once the job finishes — `result.message` is the answer then. */
  partialText?: string;
  result?: unknown;
  error?: string;
  /** Why it failed, for the UI to branch on — see {@link AgentErrorCode}. Absent on an ordinary
   *  failure, which is rendered as its `error` sentence and nothing more. */
  errorCode?: string;
  /** With `errorCode: 'agent_update_required'`: the Claude Code version the API demanded, and the
   *  one it refused (absent when unreadable). Never parsed out of `error`. */
  requiredVersion?: string;
  currentVersion?: string;
  /** Present while the run waits for the member's answer about locked targets, or about widening
   *  the chat target (`kind: 'scope'`) — the job stays 'running' meanwhile. */
  ask?: AgentLockAsk | AgentScopeAsk;
  createdAt: string;
  finishedAt?: string;
}
export interface EditResult {
  revisionId: string;
  changed: boolean;
}
export interface AgentAction {
  tool: string;
  target?: string;
}
/** A prior chat turn sent back as conversational memory (chat text only — never the document). */
export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}
/** Chat target scope: 'part' = the open part, 'artefact' = its whole artefact, 'bid' = all artefacts. */
export type ChatScope = 'part' | 'artefact' | 'bid'
/** The chat's KB toggles as a skill run carries them (Corp KB omitted = the skill's default). */
export type SkillKb = { bidKb?: boolean; corporateKb?: boolean }
/**
 * An image attached to a chat message (pasted, dropped, or picked in the composer). It rides
 * INLINE with the message to the local agent and is never stored: unlike an image dropped in the
 * document — which is uploaded to the backend blob store, see lib/image-upload.ts — an attachment
 * is part of the prompt, so it stays on the local generation side and never reaches the server.
 */
export interface ChatImage {
  name: string
  /** One of lib/chat-images.ts#ACCEPTED_IMAGE_TYPES. */
  contentType: string
  /** Raw base64, no `data:` prefix. */
  dataBase64: string
};
/**
 * A PowerPoint deck attached to a chat message (AB#452, lib/chat-decks.ts). Unlike an image it does
 * not ride inline: the composer stages it on the backend (`POST /v1/slides/imports/staged`, kept an
 * hour for the member) and the message carries only its id and name, which the agent passes to
 * `import_pptx { source: { stagedId } }`.
 */
export interface ChatDeck {
  stagedId: string
  name: string
}
/**
 * One knowledge-base document a chat answer consulted, as the passerelle collected it while the
 * agent ran (`ToolCtx.sources`). Deduplicated by file: several excerpts of the same document are
 * ONE entry with a `hits` count.
 *
 * "Consulted", not "cited" — this is what the search tools returned to the agent, which is
 * knowable; whether the model actually used a given excerpt is not.
 */
export interface SourceRef {
  /** Which knowledge base answered — the dialog's two sections. */
  kb: 'bid' | 'corporate'
  /** The KB source's label (a SharePoint site, a corporate source). `''` when unknown. */
  source: string
  /** Folders between the document library and the file, outermost first. */
  folders: string[]
  file: string
  /** http(s) link to the file. Absent ⇒ the row is not clickable (an upload, or a masked source). */
  url?: string
  /** How many excerpts of this file the agent received. */
  hits: number
  /** The excerpts themselves (capped server-side at 3 × 300 chars). */
  snippets: string[]
}
/** Result of an agentic chat run (the agent may read/create/modify parts). */
export interface AgentChatResult {
  message: string;
  actions: AgentAction[];
  /** Absent when no KB search ran, or for the OpenCode agent (see DEBT.md) — never `[]`. */
  sources?: SourceRef[];
  /** What was left undone because of a lock, and why. Absent when nothing was. */
  undone?: AgentUndoneTarget[];
  /** The chat target the run ended on, when the member widened it mid-run. Absent = unchanged. */
  scope?: ChatScope;
  /** Claude only (AB#380): the local session this turn ran in — sent back on the thread's next
   *  message so the agent resumes it instead of re-reading a flattened transcript. */
  sessionId?: string;
  tokensIn: number;
  tokensOut: number;
  costUsd?: number;
  durationMs: number;
  provider: string;
  model: string;
  /** AB#401: the run was routed to a lighter model (the picker was on its default) — the answer
   *  offers "Redo with Opus". */
  routed?: boolean;
  /** The run's usage record id, when stored — the `redoOf` of its redo. */
  usageId?: string;
}
/** Model routing fields of `api.agentChat` (AB#401). */
export interface AgentChatRouting {
  modelIsDefault?: boolean
  partKind?: PartKind
  redoOf?: string
  /** The PowerPoint decks of THIS message, already staged (lib/chat-decks.ts). */
  decks?: ChatDeck[]
}
export interface AskJobResult {
  answer: string;
  citations: { sourceId: string; sourceName: string }[];
}
export interface ImproveResult {
  improved: string;
}
export interface SvgResult {
  svg: string;
}

/** PUT one template-set component at one scope (`quiet` — see api.putTemplateBrand). */
function putTemplate(
  component: TemplateComponent,
  body: Record<string, unknown>,
): Promise<{ ok: true; trimmed?: TemplateTrim }> {
  return request<{ ok: true; trimmed?: TemplateTrim }>(
    `/template-sets/${component}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    { quiet: true },
  );
}

export const api = {
  me: () => request<Me>('/me'),
  /** The caller's own settings (AB#388). `quiet`: a failure is swallowed by lib/user-settings.ts —
   *  the local cache keeps working, so nothing is worth a dialog. */
  meSettings: () => request<{ settings: UserSettings }>('/me/settings', {}, { quiet: true }),
  /** Merge `settings` over the caller's own (400 on an unknown key or a bad value). */
  patchMeSettings: (settings: UserSettings) =>
    request<{ settings: UserSettings }>(
      '/me/settings',
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings }) },
      { quiet: true },
    ),
  bids: () => request<{ bids: Bid[] }>('/bids'),
  /** Only `name` is required; the server normalises a Thor ID to `OP# 12345678` (and 400s a
   *  malformed one — the form never sends one). */
  createBid: (fields: { name: string } & BidMetaFields) =>
    request<Bid>('/bids', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    }),
  deleteBid: (id: string) =>
    request<{ ok: true }>(`/bids/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /**
   * Rename the bid, and set, change or clear its Thor ID / client — editor/admin (403 otherwise).
   * A rename changes the display name only: the answer carries the same id, so the bid's URL stays.
   * An absent field is left as it is; `''` clears the Thor ID or client (a blank name is a 400).
   * `quiet`: the card states a refusal in place.
   */
  updateBidMetadata: (id: string, patch: BidPatchFields) =>
    request<{ bid: Bid }>(
      `/bids/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
      { quiet: true },
    ),
  /**
   * Which opportunity an OP# is (Dealtracker's Thor flow, called by the backend — the flow's URL is
   * a credential and never reaches the browser). The id rides in the PATH, so it is encoded: its `#`
   * and space would otherwise end the path. Never throws: a miss or an outage is a result the field
   * words for the member, not an error dialog.
   */
  lookupThorOpportunity: async (thorId: string): Promise<ThorLookupResult> => {
    try {
      const hit = await request<{ thorId: string; client: string | null; opportunity: string | null }>(
        `/thor/${encodeURIComponent(thorId)}`,
        {},
        { quiet: true },
      );
      return { status: 'found', thorId: hit.thorId, client: hit.client, opportunity: hit.opportunity };
    } catch (e) {
      // 404 = Thor has no such opportunity. Everything else (502, network, …) = Thor unreachable.
      return isApiError(e) && e.status === 404 ? { status: 'not_found' } : { status: 'unavailable' };
    }
  },

  // ─── Bid membership (who is on this bid, and what they may do here) ──────────────────
  /** The bid's roster. Any member may read it — an editor and a viewer see the team too. */
  bidMembers: (bid: string) =>
    request<{ members: BidMember[] }>(`/bids/${encodeURIComponent(bid)}/members`),
  /** Invite by corporate address, or re-profile someone already there. Admin only (403 otherwise). */
  addBidMember: (bid: string, email: string, role?: BidRole, profile?: Profile) =>
    request<{ member: BidMember }>(`/bids/${encodeURIComponent(bid)}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ...(role ? { role } : {}), ...(profile ? { profile } : {}) }),
    }, { quiet: true }),
  /** Change role and/or profile; an omitted field is left as it is. Admin only. */
  updateBidMember: (bid: string, email: string, patch: { role?: BidRole; profile?: Profile }) =>
    request<{ member: BidMember }>(
      `/bids/${encodeURIComponent(bid)}/members/${encodeURIComponent(email)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
      { quiet: true },
    ),
  /** Admin only. Removing the last admin is refused by the backend with a 409 `last_admin`. */
  removeBidMember: (bid: string, email: string) =>
    request<{ ok: true }>(
      `/bids/${encodeURIComponent(bid)}/members/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
      { quiet: true },
    ),
  // ─── Bid site (where the bid's source material lives in SharePoint) ──────────────────
  /** The pinned location, or `{ site: null }` when nobody has set one — an ordinary answer. */
  bidSite: (bid: string) =>
    request<{ site: BidSite | null }>(`/bids/${encodeURIComponent(bid)}/site`),
  /**
   * Pin (or re-pin) it — editor/admin, 403 otherwise. `quiet` because a refusal here is a RULE the
   * picker states in place; the global error dialog would call it a malfunction.
   */
  setBidSite: (bid: string, site: BidSite) =>
    request<{ site: BidSite }>(
      `/bids/${encodeURIComponent(bid)}/site`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(site),
      },
      { quiet: true },
    ),
  /**
   * Detach the bid from its site — the same editor/admin write as pinning one, so also `quiet`.
   * Deletes a POINTER: SharePoint keeps the folder, its documents and their permissions.
   */
  clearBidSite: (bid: string) =>
    request<{ site: null }>(
      `/bids/${encodeURIComponent(bid)}/site`,
      { method: 'DELETE' },
      { quiet: true },
    ),

  // ─── Browsing SharePoint (the backend calls Graph on the member's behalf) ────────────
  /**
   * The three reads behind the bid-site picker and pane. All bid-scoped, because the permission
   * that authorises them is: the caller must be a member of the bid, or this would be a
   * general-purpose SharePoint search for anyone holding a Niobe token. Beyond that Niobe grants
   * NOTHING — the on-behalf-of token acts as the member, so SharePoint decides every row.
   *
   * All three are `quiet`. A 403 is SharePoint's own answer ("not shared with you"), and the pane
   * turns it into a request-access hand-off; the global error dialog would call a permission a
   * malfunction. The ids ride in the QUERY, not the path: a site id contains commas and a drive id
   * starts with `b!`, and path-encoding those is a class of bug nobody needs.
   *
   * Sites matching `q`. A term is REQUIRED: "list every site" is `/sites/delta`, an application-only
   * Graph call that 403s on a delegated token, so an empty term answers `{ sites: [] }`.
   */
  spSites: (bid: string, q: string) =>
    request<{ sites: SpSite[] }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/sites?q=${encodeURIComponent(q)}`,
      {},
      { quiet: true },
    ),
  /**
   * Resolve a pasted SharePoint address (sharing link, library/folder/file URL, site URL) as the
   * caller. `{ resolved: null }` is an ordinary answer — a link Niobe could not open with this
   * member's account — not an error status. Refusals map exactly like the reads around it.
   */
  spResolve: (bid: string, url: string) =>
    request<{ resolved: SpResolved | null }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/resolve?url=${encodeURIComponent(url)}`,
      {},
      { quiet: true },
    ),
  /** The site's document libraries — the level below a site, and where a bid folder must live. */
  spDrives: (bid: string, siteId: string) =>
    request<{ entries: SpEntry[] }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/drives?siteId=${encodeURIComponent(siteId)}`,
      {},
      { quiet: true },
    ),
  /** What is inside a library (`itemId: 'root'`) or one of its folders. Files included. */
  spChildren: (bid: string, driveId: string, itemId: string) =>
    request<{ entries: SpEntry[] }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/children` +
        `?driveId=${encodeURIComponent(driveId)}&itemId=${encodeURIComponent(itemId)}`,
      {},
      { quiet: true },
    ),
  /**
   * Create a folder in one of the bid's SharePoint drives — the "+ New folder" affordance in the
   * export destination picker. Editor/admin only, `quiet` for the same reason as the reads above:
   * a name collision (`sharepoint_conflict`) or an unusable name (`invalid`) is a RULE the picker
   * states in place, not a malfunction.
   */
  spCreateFolder: (bid: string, driveId: string, parentItemId: string, name: string) =>
    request<{ entry: SpEntry }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/folders`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driveId, parentItemId, name }),
      },
      { quiet: true },
    ),

  /**
   * Click-to-index: send the member's selection (from the pane above) into the bid's own KB.
   * Editor/admin only — `quiet` because a viewer's 403 here is the same RULE the site-pin write
   * states in place, not a malfunction. Detached: answers 202 with a job id to poll below.
   */
  spIndex: (bid: string, driveId: string, items: SpIndexSelection[]) =>
    request<{ jobId: string; status: SpIndexJob['status'] }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/index`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driveId, items }),
      },
      { quiet: true },
    ),
  /** Poll a click-to-index job. Any bid member may read it — the counters carry no content. */
  spIndexJob: (bid: string, jobId: string) =>
    request<SpIndexJob>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/index/${encodeURIComponent(jobId)}`,
      {},
      { quiet: true },
    ),
  /**
   * "Interrupt indexation": same editor/admin write bar as starting the run — `quiet` for the
   * same reason. A no-op, not an error, once the run has already settled.
   */
  spCancelIndex: (bid: string, jobId: string) =>
    request<{ ok: boolean }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/index/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
      { quiet: true },
    ),

  /**
   * De-index: remove one previously-indexed file, or an entire folder's worth (walked and removed
   * server-side), from the bid's own KB. Same editor/admin write bar as indexing it — `quiet`, same
   * reasoning as `spIndex`. Synchronous (a plain 200): there is no download/parse/embed behind it.
   */
  spDeIndex: (bid: string, driveId: string, item: SpIndexSelection) =>
    request<{ deleted: number }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/index`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driveId, item }),
      },
      { quiet: true },
    ),

  /**
   * Export the open part/deliverable/bid straight into the bid's SharePoint — its default
   * `niobe/exports` folder (created server-side on first use) when `destination` is omitted, or the
   * member's own picked folder otherwise. The other half of the plain `/export` download
   * (lib/download.ts#downloadExport): same scope/format, a SharePoint upload instead of a browser
   * save. Editor/admin only — `quiet`, same reasoning as every other write in this section; a
   * refusal (`no_destination`, `unsupported_format`, `sharepoint_conflict`, …) is for ExportMenu to
   * render in place.
   */
  /**
   * The base name (no extension) the server gives an export of this selection by default —
   * `"<name> - v<version>"`, its own rule (backend/domain/export.ts#exportFilename) — so ExportMenu
   * prefills its file-name field without a copy of that rule here. `quiet`: a failure only leaves the
   * field empty, which the dialog already explains.
   */
  spExportName: (bid: string, sel: { scope: string; artefactId?: string; name?: string }) => {
    const qs = new URLSearchParams({ scope: sel.scope })
    if (sel.artefactId) qs.set('artefactId', sel.artefactId)
    if (sel.name) qs.set('name', sel.name)
    return request<{ name: string }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/export/name?${qs.toString()}`,
      {},
      { quiet: true },
    )
  },

  spExport: (
    bid: string,
    body: {
      scope: string
      format: string
      /** Which deliverable, for `scope: 'artefact'`. */
      artefactId?: string
      /** The room key "<bid>/<artefact>/<part>", for `scope: 'part'`. */
      name?: string
      includeReviews?: boolean
      destination?: { driveId: string; itemId: string }
      /** The file's base name as the member typed it — the server appends the rendered extension. */
      filename?: string
      /** `fail` answers 409 `sharepoint_conflict` on a name already there; omitted, the server replaces — always send it. */
      conflict?: 'fail' | 'replace'
    },
  ) =>
    request<{ entry: SpEntry }>(
      `/bids/${encodeURIComponent(bid)}/sharepoint/export`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      { quiet: true },
    ),

  // ─── The bid's click-to-index chunk ceiling (plans/BID_SITE_INDEXING.md) ─────────────
  /** Open to any member — knowing the ceiling and how much is used is information, not a privilege. */
  indexLimit: (bid: string) =>
    request<{ maxChunks: number; used: number }>(`/bids/${encodeURIComponent(bid)}/index-limit`),
  /** Raise (or lower) it — TENANT admin only. `quiet`: a non-admin's 403 is a rule the card states
   *  in place, not a malfunction. */
  setIndexLimit: (bid: string, maxChunks: number) =>
    request<{ maxChunks: number; used: number }>(
      `/bids/${encodeURIComponent(bid)}/index-limit`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxChunks }),
      },
      { quiet: true },
    ),

  // ─── Version retention (AB#244 API, AB#245 UI) ─────────────────────────────────────────────
  /** The tenant policy — ages, day window, zone, default counts. Tenant admin only (403). */
  tenantRetention: () => request<TenantRetentionView>('/admin/retention', {}, { quiet: true }),
  /** Replace the tenant policy. `quiet`: a 400 `invalid` is worded in place by the page. */
  setTenantRetention: (policy: RetentionPolicy) =>
    request<TenantRetentionView>(
      '/admin/retention',
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(policy) },
      { quiet: true },
    ),
  /** The bid's merged policy + the tenant's + its count override. Bid admin profile (403). */
  bidRetention: (bid: string) =>
    request<BidRetentionView>(`/bids/${encodeURIComponent(bid)}/retention`, {}, { quiet: true }),
  /** Set the bid's counts (index-aligned to the tenant tiers, null = the tenant's; [] = none). */
  setBidRetention: (bid: string, counts: BidRetentionCounts) =>
    request<BidRetentionView>(
      `/bids/${encodeURIComponent(bid)}/retention`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ counts }) },
      { quiet: true },
    ),
  /** Dry run of the bid's SAVED policy: how many restore points it would remove now, in how many parts. */
  bidRetentionPreview: (bid: string) =>
    request<RetentionPreview>(`/bids/${encodeURIComponent(bid)}/retention/preview`, {}, { quiet: true }),

  /**
   * Corporate directory lookup for the people picker.
   *
   * Answers `{ people: [] }` rather than failing whenever the directory is unavailable (the Graph
   * permission it needs may never be consented in this tenant), so the caller must treat an empty
   * list as "type the address yourself", never as "no such person".
   */
  directorySearch: (q: string) =>
    request<{ people: DirectoryPerson[] }>(`/directory/search?q=${encodeURIComponent(q)}`),
  /**
   * A colleague's small profile photo, or `null` when there is none to show. Not through
   * `request()` — that is JSON-only, and it reports failures: a missing photo (404, which is also
   * what a tenant without the Graph permission answers) or a failed fetch is NOT an error here,
   * the caller just shows initials. Fetched with the Bearer because an `<img src>` cannot send it.
   */
  directoryPhoto: async (email: string): Promise<Blob | null> => {
    try {
      const headers = new Headers({ Accept: 'image/*' });
      const token = await freshToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v);
      const res = await fetch(apiUrl(`/v1/directory/photo?email=${encodeURIComponent(email)}`), { headers });
      noteFloorHeaders(res.headers);
      if (!res.ok) return null;
      const blob = await res.blob();
      return blob.size > 0 && blob.type.startsWith('image/') ? blob : null;
    } catch {
      return null;
    }
  },
  /**
   * TEMPORARY diagnostic (plans/BID_SITE_MEMBER_SYNC.md, "still open" #2) — not a product
   * capability. Server-side gated to a single account; delete alongside
   * components/GraphDiagnosticsSection.tsx once the spike is resolved.
   */
  diagnosticGroupMembers: (groupId: string) =>
    request<{ ok: true; members: DiagnosticGroupMember[] }>(
      `/admin/diagnostics/group-members?groupId=${encodeURIComponent(groupId)}`,
    ),
  agents: () => request<{ agents: string[] }>('/agents'),
  artefacts: (bid: string) =>
    request<{ artefacts: ArtefactOutline[] }>(`/artefacts?bid=${encodeURIComponent(bid)}`),
  // Artefact + item (part) CRUD. Rename changes the display title only —
  // the room key (id) and its CRDT/history are immutable.
  /**
   * `folderId` is the explorer folder it lands in — omit it (or null) for the root level. `type`
   * is fixed for the life of the artefact (prose by default, server-side too); a slides artefact
   * is seeded with one slides part instead of a text one.
   */
  createArtefact: (bid: string, title: string, folderId: string | null = null, type?: ArtefactType) =>
    request<{ artefactId: string; room: string }>('/artefacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, title, folderId, ...(type ? { type } : {}) }),
    }),
  /**
   * Create an artefact as a one-way copy of a library template (AB#267/AB#270): its parts and its
   * type come from the template, so no `type` is sent. `room` is the first copied part — `null`
   * when the template holds none.
   */
  createArtefactFromTemplate: (bid: string, title: string, folderId: string | null, templateId: string) =>
    request<{ artefactId: string; room: string | null }>('/artefacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, title, folderId, fromTemplate: templateId }),
    }),
  /** The template library's catalogue (categories ≤ 2 levels + templates), open to every member. */
  artefactTemplates: () => request<TemplateCatalogue>('/artefact-templates'),
  /**
   * `kind` decides what the new part's room holds — prose (the default) or a grid — and is fixed
   * for the life of the part. `grid` seeds a grid part's structure; the "convert this table to a
   * grid part" action sends the parsed pipe-table here.
   */
  createPart: (
    bid: string,
    artefactId: string,
    title: string,
    kind: PartKind = 'markdown',
    grid?: GridSnapshot,
  ) =>
    request<{ partId: string; room: string; kind: PartKind }>('/artefacts/parts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, artefactId, title, kind, ...(grid ? { grid } : {}) }),
    }),
  /**
   * A deck's PowerPoint templates (AB#445): its default (template, master) and the bid's library,
   * for any member who can read the room. The slides editor reads it first, then only the views of
   * the pairs the deck's slides use. `quiet` — a failure falls back to the default theme and the
   * built-in layouts rather than popping an error over the canvas. (The editor no longer reads
   * `GET /v1/bids/:bid/slides-template`, which stays for older niobe-mcp / passerelle builds.)
   */
  deckTemplates: (room: string) =>
    request<DeckTemplates>(`/slides/templates?room=${encodeURIComponent(room)}`, {}, { quiet: true }),
  /** A deck's slides in order: id, layout, template, title (any member; the template view's Preview, AB#457). */
  slidesOf: (room: string) => request<{ room: string; slides: SlideListing[] }>(`/slides?room=${encodeURIComponent(room)}`, {}, { quiet: true }),
  /** ONE slide as plain data, and the template and master it is drawn on (the template view's Preview, AB#457). */
  slideSnapshot: (room: string, slide: string) =>
    request<{ slide: ServerSlide; index: number; count: number; template: { id: string; master: number } | null }>(
      `/slides/snapshot?room=${encodeURIComponent(room)}&slide=${encodeURIComponent(slide)}`,
      {},
      { quiet: true },
    ),
  /**
   * What the editor paints for one template of the bid's library on one of its masters: its theme,
   * that master's drawing and that master's layouts (with ids) — the payload the bid-wide
   * slides-template route answers, per (template, master). `quiet`, as above.
   */
  officeTemplateSlidesView: (bid: string, id: string, master: number) =>
    request<{ template: SlidesTemplateLike | null }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/slides-view?master=${master}`,
      {},
      { quiet: true },
    ),
  /**
   * Search the Capgemini icon library (`search_icons`): every hit, best first, plus the library's
   * categories (keys, EN + FR labels). Accents and case are ignored server-side, and a query in
   * English or French finds the same icons. Beta: 403 for a non-tester.
   */
  slideIcons: (q: string) =>
    request<{ icons: SlideIconSummary[]; total: number; categories: Array<SlideIconCategory & { count: number }> }>(
      `/slides/icons${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`,
      {},
      { quiet: true },
    ),
  /**
   * The PowerPoint import (plans/PPTX_IMPORT.md I2, components/ImportPowerPointDialog.tsx). All
   * `quiet`: the dialog states every refusal in place — `encrypted`, `not_pptx`, `macro_enabled`,
   * `too_large`, a SharePoint refusal, a selection over 50 slides, an expired staged deck.
   */
  stageSlideImport: (source: SlideImportSource) =>
    request<StagedDeck>(
      '/slides/imports/staged',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(source) },
      { quiet: true },
    ),
  /** Import a staged deck into a slides part at `at` (0-based; absent = the end) → 202 `{ importId }`. */
  startSlideImport: (body: {
    stagedId: string
    room: string
    at?: number
    mode: SlideImportMode
    slides?: number[]
    includeHidden?: boolean
    target?: DeckTemplateRef
    destination?: SlideImportDestination
  }) =>
    request<{ importId: string }>(
      '/slides/imports',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      { quiet: true },
    ),
  slideImport: (id: string) => request<SlideImportJob>(`/slides/imports/${encodeURIComponent(id)}`, {}, { quiet: true }),
  /** The bid's last jobs this person may see, newest first (AB#462): the Jobs section reads it on its own. */
  bidJobs: (bid: string) => request<{ jobs: BidJob[] }>(`/bids/${encodeURIComponent(bid)}/jobs`, {}, { quiet: true }),
  /** A slides part's imports, newest first: their reports and the originals still kept (AB#446). */
  slideImports: (room: string) =>
    request<{ imports: RoomSlideImport[] }>(`/slides/imports?room=${encodeURIComponent(room)}`, {}, { quiet: true }),
  /**
   * The original render (PNG) of an imported slide, while it is kept — "Compare with original".
   * Not through `request()`, which is JSON-only; fetched with the Bearer because an `<img src>`
   * cannot send it. A refusal rejects with the backend's `status` and `code` (410 `renders_expired`).
   */
  slideImportRender: async (id: string, sourceIndex: number): Promise<Blob> => {
    const headers = new Headers({ Accept: 'image/png' })
    const token = await freshToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    for (const [k, v] of Object.entries(clientHeaders())) headers.set(k, v)
    const res = await fetch(apiUrl(`/v1/slides/imports/${encodeURIComponent(id)}/renders/${sourceIndex}`), { headers })
    noteFloorHeaders(res.headers)
    if (!res.ok) {
      let code = ''
      try {
        code = ((await res.json()) as { error?: { code?: string } }).error?.code ?? ''
      } catch {
        /* non-JSON error body */
      }
      throw Object.assign(new Error(`API ${res.status} on the original render`), { status: res.status, code, serverMessage: '' })
    }
    return res.blob()
  },
  /**
   * Run a `failed` / `locked` import again (its renders too unless they succeeded) while its source
   * is kept — or a converted one whose cut-outs wait for the renders ("Retry renders").
   */
  retrySlideImport: (id: string) =>
    request<{ importId: string }>(
      `/slides/imports/${encodeURIComponent(id)}/renders/retry`,
      { method: 'POST' },
      { quiet: true },
    ),
  /** The SVGs of some icons (1–200 ids), fetched lazily by the slides editor. */
  slideIconSvgs: (ids: string[]) =>
    request<{ icons: SlideIconSvg[] }>(`/slides/icons/svg?ids=${ids.map(encodeURIComponent).join(',')}`, {}, { quiet: true }),
  /** "Parts from sections": split every part of the artefact into one part per top-level §.
   *  Returns the rooms created — empty when no part held anything to split. */
  splitParts: (bid: string, artefactId: string) =>
    request<{ created: string[] }>('/artefacts/parts/split', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, artefactId }),
    }),
  renameArtefact: (bid: string, artefactId: string, title: string) =>
    request<{ ok: true }>('/artefacts', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, artefactId, title }),
    }),
  renamePart: (room: string, title: string) =>
    request<{ ok: true }>('/artefacts/parts', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room, title }),
    }),
  reorderParts: (bid: string, artefactId: string, orderedRooms: string[]) =>
    request<{ ok: true }>('/artefacts/parts/order', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, artefactId, orderedRooms }),
    }),
  /** Send ONE slide to another slides part of the bid (the sorter → navigation drag): `move` takes
   *  it out of its deck, `copy` leaves it there. It lands at the end of the target under a new id. */
  transferSlide: (fromRoom: string, slide: string, toRoom: string, mode: 'move' | 'copy') =>
    request<{ room: string; slide: string; index: number }>('/slides/transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromRoom, slide, toRoom, mode }),
    }),
  deleteArtefact: (bid: string, artefactId: string) =>
    request<{ ok: true }>(
      `/artefacts?bid=${encodeURIComponent(bid)}&artefactId=${encodeURIComponent(artefactId)}`,
      { method: 'DELETE' },
    ),
  // ─── Artefact templates (the hidden template library — lib/template-library.ts) ──────
  /**
   * "Publish as template" (artefact templates 4/8) — tenant admins only. `category` = the library
   * folder titles, created when missing ([] = the root); `replaces` = the template to replace,
   * omitted = a new one. `quiet`: the dialog states every refusal (403/404/409/422) inline.
   */
  publishArtefactTemplate: (body: {
    bidId: string;
    artefactId: string;
    name: string;
    description: string;
    category: string[];
    replaces?: string;
  }) =>
    request<{ templateId: string; version: number; replaced: boolean }>(
      '/artefact-templates/publish',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      { quiet: true },
    ),
  /** Change a template's name and/or description (tenant admins only, 403 otherwise). No version change. */
  updateArtefactTemplate: (templateId: string, patch: { name?: string; description?: string }) =>
    request<{ ok: true }>(`/artefact-templates/${encodeURIComponent(templateId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  // ─── Explorer folders (virtual grouping of the artefacts in the left panel) ──────────
  folders: (bid: string) => request<{ folders: Folder[] }>(`/folders?bid=${encodeURIComponent(bid)}`),
  createFolder: (bid: string, title: string, parentFolderId: string | null) =>
    request<{ folderId: string }>('/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, title, parentFolderId }),
    }),
  renameFolder: (bid: string, folderId: string, title: string) =>
    request<{ ok: true }>('/folders', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, folderId, title }),
    }),
  /** Re-parent a folder (null = root) and/or rank it among its siblings. */
  moveFolder: (bid: string, folderId: string, parentFolderId: string | null, index?: number) =>
    request<{ ok: true }>('/folders/move', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, folderId, parentFolderId, index }),
    }),
  /** Delete a folder — its subfolders and artefacts are lifted to its parent, never deleted. */
  deleteFolder: (bid: string, folderId: string) =>
    request<{ ok: true }>(
      `/folders?bid=${encodeURIComponent(bid)}&folderId=${encodeURIComponent(folderId)}`,
      { method: 'DELETE' },
    ),
  /** Move an artefact into a folder (null = root) and rank it there — drag-drop and cut/paste. */
  moveArtefact: (bid: string, artefactId: string, folderId: string | null, index?: number) =>
    request<{ ok: true }>('/artefacts/move', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, artefactId, folderId, index }),
    }),
  deletePart: (room: string) =>
    request<{ ok: true }>(`/artefacts/parts?room=${encodeURIComponent(room)}`, { method: 'DELETE' }),
  ask: (bid: string, query: string) =>
    request<AskResult>('/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, query }),
    }),
  // Agentic calls are ASYNCHRONOUS: they return a jobId immediately; poll agentJob(id).
  /** /niobe-edit → start a background edit job. Result: { revisionId, changed }. */
  agentEdit: (room: string, prompt: string) =>
    agentRequest<{ jobId: string }>('/agent/edit', { room, prompt }),
  /** Highlighted excerpt + instruction → start a background improve job. Result: { improved }. */
  agentImprove: (selectedText: string, instruction: string, room?: string, model?: string) =>
    agentRequest<{ jobId: string }>('/agent/improve', { selectedText, instruction, room, model }),
  /** Question → start a background ask job. Result: { answer, citations }. */
  agentAsk: (bid: string, query: string) =>
    agentRequest<{ jobId: string }>('/agent/ask', { bid, query }),
  /** Prompt → start a background SVG-generation job (counted against the bid). Result: { svg }. */
  agentSvg: (prompt: string, model?: string, bid?: string) =>
    agentRequest<{ jobId: string }>('/agent/svg', { prompt, model, bid }),
  /** Agentic chat: the local agent reads/creates/modifies parts. Result: AgentChatResult.
   * `history` carries prior chat turns (conversational memory — chat text only, no document).
   * `scope` sets the target: 'part' = the open part, 'artefact' = its whole artefact, 'bid' = all artefacts.
   * `bidKb`/`corporateKb` gate whether the agent may call the bid's own knowledge base and/or the
   * company-wide one for this message only — a per-message toggle, not a sticky preference.
   * `localSkills` is Claude-only and mirrors the member's sticky "Enable local skills" preference
   * (the ⓘ agent-info modal, lib/agent-prefs.ts#getLocalSkillsEnabled) rather than a per-message
   * choice: omitted/true keeps the existing always-on behaviour, `false` turns it off.
   * `disabledSkills` is the sticky per-skill opt-out layered under `localSkills` (names from
   * `api.localSkills()`, lib/agent-prefs.ts#getDisabledLocalSkills) — omitted/empty leaves every
   * discovered skill enabled.
   * `images` are the attachments of THIS message (paste/drop/pick, see lib/chat-images.ts): the
   * passerelle hands them to the agent's own SDK and stores nothing.
   * `sessionId` is the thread's local Claude session (the previous turn's result.sessionId): the
   * passerelle resumes it and falls back to `history` when it cannot.
   * `routing` (AB#401): `modelIsDefault` = the picker is on its default (the member never chose a
   * model), so the passerelle may route a mechanical edit to Haiku / Sonnet; `partKind` = the open
   * part's kind; `redoOf` = this is a "Redo with Opus" of that routed run (never routed).
   * `decks` (AB#452) are the PowerPoint decks of THIS message, staged on the backend by the
   * composer: only `{ stagedId, name }` travels, and the agent imports them with import_pptx. */
  agentChat: (
    bid: string,
    room: string | null,
    prompt: string,
    agent: string,
    model?: string,
    history?: ChatTurn[],
    scope?: ChatScope,
    bidKb?: boolean,
    corporateKb?: boolean,
    localSkills?: boolean,
    disabledSkills?: string[],
    images?: ChatImage[],
    sessionId?: string,
    { modelIsDefault, partKind, redoOf, decks }: AgentChatRouting = {},
  ) => agentRequest<{ jobId: string }>('/agent/chat', { bid, room, prompt, agent, model, history, scope, bidKb, corporateKb, localSkills, disabledSkills, images, decks, sessionId, modelIsDefault, partKind, redoOf }),
  /** Available models for an agent (Claude aliases / Copilot live list). */
  agentModels: (agent: string) =>
    agentGet<{ models: { id: string; name: string }[] }>(`/agent/models?agent=${encodeURIComponent(agent)}`),
  /** Local skills Claude Code found on the member's machine (Claude-only). */
  localSkills: () =>
    agentGet<{ skills: LocalSkill[] }>('/agent/local-skills'),
  /** Which agent CLIs are installed on this machine (Settings → AI agent). */
  agentStatus: () => agentGet<AgentStatusResponse>('/agent/status'),
  /** Kick off (or re-attach to) an assisted install. Poll the result with agentJob(jobId), same as every other job kind. */
  triggerInstall: (agent: AgentId) => agentRequest<{ jobId: string }>('/agent/install', { agent }),
  /** Kick off (or re-attach to) an assisted sign-in: `claude auth login` or `copilot login`. */
  triggerLogin: (agent: AgentId) => agentRequest<{ jobId: string }>('/agent/login', { agent }),
  /** Kick off (or re-attach to) an assisted UPDATE — re-runs the native installer to lift a CLI
   *  below the version floor. Same job shape (and `InstallResult`) as triggerInstall. */
  triggerUpdate: (agent: AgentId) => agentRequest<{ jobId: string }>('/agent/update', { agent }),
  /**
   * Ask the passerelle to refresh the local Claude login before the member types (wake/focus).
   *
   * Fire-and-forget: it answers 202 immediately and the passerelle's own gate decides whether
   * anything runs at all. When it does, the reply carries a `jobId` — poll that job, and invalidate
   * `['agent-status']` once it leaves `running`. Invalidating on the 202 alone would re-read the
   * status BEFORE the CLI has touched the credentials, and so report the stale answer.
   */
  warmupAgent: () => agentRequest<WarmupResponse>('/agent/warmup', {}),
  /**
   * Hand the `code#state` from the Claude sign-in page to the CLI still waiting on it.
   *
   * 400 = the code isn't a `code#state` (the CLI is still waiting, so the member can retype it);
   * 404 = that sign-in is gone; 409 = the CLI already ended this attempt and a new one is needed.
   */
  submitLoginCode: (jobId: string, code: string) =>
    agentRequest<{ ok: true }>(`/agent/login/${encodeURIComponent(jobId)}/code`, { code }),
  /** Poll one job's status/result (scoped to this session). */
  agentJob: (id: string) => agentGet<AgentJob>(`/agent/jobs/${encodeURIComponent(id)}`),
  /** Stop button: cancel a running job — aborts the in-flight local-agent SDK call server-side.
   *  Idempotent: cancelling a job that already finished (or a second click) is a harmless no-op. */
  cancelJob: (id: string) => agentRequest<AgentJob>(`/agent/jobs/${encodeURIComponent(id)}/cancel`, {}),
  /** Answer a paused run's lock question (`AgentJob.ask`). 409 = it is no longer pending (answered,
   *  timed out, or the run ended) — harmless, the run already moved on. */
  answerJob: (id: string, askId: string, choice: AgentLockChoice | AgentScopeChoice) =>
    agentRequest<{ ok: true }>(`/agent/jobs/${encodeURIComponent(id)}/answer`, { askId, choice }),
  /** This session's recent jobs (running + finished) — re-attach after a refresh. */
  agentJobs: (params?: { room?: string; bid?: string; status?: AgentJobStatus }) => {
    const qs = new URLSearchParams();
    if (params?.room) qs.set('room', params.room);
    if (params?.bid) qs.set('bid', params.bid);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return agentGet<{ jobs: AgentJob[] }>(`/agent/jobs${q ? `?${q}` : ''}`);
  },
  /** Upload an image (paste/drop/file) → content-addressed blob; returns its /v1/blobs URL. */
  uploadImage: (contentType: string, dataBase64: string) =>
    request<{ id: string; url: string; contentType: string }>('/blobs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType, dataBase64 }),
    }),
  /**
   * List a bid's indexed knowledge sources → `GET /v1/sources?bid=`. Bid-scoped and
   * identity-guarded server-side (the backend returns 403 when the caller lacks bid access).
   */
  listSources: (bid: string) =>
    request<{ sources: KbSource[] }>(`/sources?bid=${encodeURIComponent(bid)}`),
  // ─── Corporate KB source catalogue (tenant-admin only) ───────────────────────
  // Every call here 403s for a non-admin server-side; the client gate in KbSourcesSection is an
  // affordance only. Writes pass `{ quiet: true }` because a refusal is a RULE (422 with the
  // domain's own wording) rendered in place by the form, not an error to report globally.
  /** The catalogue — every source, enabled or not. */
  kbSources: () => request<{ sources: KbSourceRecord[] }>('/kb/sources'),
  createKbSource: (input: NewKbSourceInput) =>
    request<KbSourceSaved>(
      '/kb/sources',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
      { quiet: true },
    ),
  updateKbSource: (id: string, patch: KbSourcePatchInput) =>
    request<KbSourceSaved>(
      `/kb/sources/${encodeURIComponent(id)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) },
      { quiet: true },
    ),
  /** Removes the catalogue row and its sync bookkeeping. Its indexed CHUNKS are removed by the
   *  NEXT indexation run, which purges every source the catalogue no longer names — deleting ~293k
   *  rows on a JSONB predicate is a batch job, not an HTTP request. */
  deleteKbSource: (id: string) =>
    request<{ deleted: boolean }>(
      `/kb/sources/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      { quiet: true },
    ),
  /** Durable per-source indexation state — survives a backend restart, and sees the runs launched
   *  by the kb-sync-corporate job in another process (which the in-process job endpoint never does). */
  kbSyncStatus: () => request<{ sources: KbSourceSyncStatus[] }>('/kb/sync/status'),
  /** The crawl lease: "active as X until hh:mm" or paused. Tenant-admin only server-side. */
  kbCrawlStatus: () => request<KbCrawlStatus>('/admin/kb-crawl/status'),
  /** One colleague's request counts + last Desktop build (Settings → User activity). Tenant-admin
   *  only server-side: a non-admin gets a 403 having caused no query at all. */
  getUserActivity: (email: string) =>
    request<UserActivity>(`/admin/user-activity?email=${encodeURIComponent(email)}`),
  /** Resolve a pasted site URL. `site: null` means "that address resolves to nothing" — ordinary,
   *  not a failure. */
  kbResolveSite: (url: string) =>
    request<{ site: { id: string; displayName: string; webUrl: string } | null }>(
      `/kb/sharepoint/site-by-path?url=${encodeURIComponent(url)}`,
      {},
      { quiet: true },
    ),
  /** A site's document libraries, so the drive id is picked and never typed. */
  kbSiteDrives: (siteId: string) =>
    request<{ drives: KbSharepointDrive[] }>(
      `/kb/sharepoint/drives?siteId=${encodeURIComponent(siteId)}`,
      {},
      { quiet: true },
    ),
  /** The room's restore points. `historyId` = the history entry a restore point belongs to — what
   *  makes that entry restorable on every part kind (absent on untagged versions). `quiet`: the
   *  History modal only disables its actions when this cannot be read. */
  versions: (room: string, page: { before?: number; limit?: number } = {}) =>
    request<{ versions: VersionMeta[] }>(
      `/versions?room=${encodeURIComponent(room)}` +
        (page.before !== undefined ? `&before=${page.before}` : '') +
        (page.limit !== undefined ? `&limit=${page.limit}` : ''),
      {},
      { quiet: true },
    ),
  /** What a version changed since the previous SURVIVING one (AB#240). `quiet`: the History modal
   *  says in place that the change could not be read. */
  versionDiff: (room: string, version: number) =>
    request<VersionDiff>(`/versions/${version}/diff?room=${encodeURIComponent(room)}`, {}, { quiet: true }),
  /** Tag / untag (`tag: null`) and comment / clear (`comment: null`) a version (AB#239). An absent
   *  field is kept. `quiet`: the History modal reports the refusal itself. */
  updateVersion: (room: string, version: number, patch: { tag?: VersionTag | null; comment?: string | null }) =>
    request<{ version: VersionMeta }>(
      `/versions/${version}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, ...patch }),
      },
      { quiet: true },
    ),
  /** Flatten the room's history (AB#242): delete every version older than `keep`, tagged and named
   *  ones aside. Irreversible — `dryRun` only counts. Bid admin or tenant admin. `quiet`: the flatten
   *  dialog words the refusals itself — 403, 404, 409 `room_busy`. */
  flattenVersions: (room: string, keep: number, dryRun: boolean) =>
    request<FlattenCounts>(
      '/versions/flatten',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, keep, dryRun }),
      },
      { quiet: true },
    ),
  saveVersion: (room: string, label: string) =>
    request<{ version: number }>('/versions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room, label }),
    }),
  /** Restore a part in place to a version (a restore point older than the log, AB#241). `quiet`:
   *  the caller reports the refusals itself — 404, 409 `room_busy`. */
  restore: (room: string, version: number) =>
    request<{ restoredTo: number; snapshotted: number }>(
      '/restore',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, version }),
      },
      { quiet: true },
    ),
  /**
   * Restore a part in place to the state a history entry left it in (its tagged restore point).
   * `quiet`: the caller reports the refusals itself — 404 `no_restore_point`, 409 `room_busy`.
   */
  restoreHistoryEntry: (room: string, historyId: string) =>
    request<{ restoredTo: number; snapshotted: number }>(
      '/restore',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, historyId }),
      },
      { quiet: true },
    ),
  /**
   * "Restore as part": a new READ-ONLY part holding the source's state at one history entry, right
   * after it. The source is a history entry's restore point, a version (older than the log), or —
   * prose only — the markdown the client rebuilt from the log with the entry's instant. `title` is
   * formatted client-side (the member's local time). `quiet`: the caller reports the refusals.
   */
  restoreCopy: (
    room: string,
    source: { historyId: string } | { version: number } | { markdown: string; at: string },
    title: string,
  ) =>
    request<{ partId: string; room: string; kind: PartKind; title: string }>(
      '/parts/restore-copy',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, ...source, title }),
      },
      { quiet: true },
    ),
  /** Live presence for a bid: distinct connected users (by name) per part room — the nav's
   *  per-part counts — plus `users`, the bid roster the top bar renders as avatars: one entry
   *  per connected member, `agentBusy` while their console agent has a task in flight. */
  presence: (bid: string) =>
    request<{
      rooms: Record<string, { users: string[] }>
      users: Array<{ id: string; name: string; agentBusy: boolean }>
    }>(`/presence?bid=${encodeURIComponent(bid)}`),
  /** A part's § outline, plus the `offset` its numbering starts at. Numbering is continuous
   *  across a deliverable, so part 2 opens where part 1 stopped — the IHM numbers the live
   *  editor doc itself and needs that integer, which depends on rooms it never opens. */
  sections: (room: string) =>
    request<{
      room: string
      offset: number
      sections?: Array<{ number: string; title: string; level: number; chars: number }>
    }>(`/sections?room=${encodeURIComponent(room)}`),
  /** Word/line counts per part room of a bid (document & part statistics). */
  stats: (bid: string) =>
    request<{ rooms: Record<string, { words: number; lines: number }> }>(
      `/stats?bid=${encodeURIComponent(bid)}`,
    ),
  // ─── Template sets (bid → corporate resolution) ─────────────────────────────
  /** One scope's template set: its own values, each component's effective source, edit rights.
   *  `owner` = bid id (bid scope); omitted for corporate. */
  templateSet: (scope: TemplateScope, owner?: string | null) =>
    request<TemplateSetView>(
      `/template-sets?scope=${encodeURIComponent(scope)}` +
        (owner ? `&owner=${encodeURIComponent(owner)}` : ''),
    ),
  /**
   * The template-set writes are `quiet`: BrandingCard states a failure in place. A bid's Word and
   * PowerPoint templates are its bid Office templates now (`officeTemplates` below).
   */
  putTemplateBrand: (scope: TemplateScope, owner: string | null, brand: Brand) =>
    putTemplate('brand', { scope, owner, brand }),
  /** Drop one component at the bid scope, so it resolves from the corporate scope again. */
  clearTemplateComponent: (scope: TemplateScope, owner: string | null, component: TemplateComponent) =>
    request<{ ok: true }>(
      `/template-sets/${component}?scope=${encodeURIComponent(scope)}` +
        (owner ? `&owner=${encodeURIComponent(owner)}` : ''),
      { method: 'DELETE' },
      { quiet: true },
    ),
  // ─── Corporate template catalogue (tenant admin writes, AB#220) ─────────────
  /**
   * The ACTIVE corporate templates of a kind, in catalogue order (any signed-in member);
   * `includeRetired` (retired entries too) is the tenant admin's — 403 otherwise. `quiet`: both
   * callers say a failure in place, and without the list BrandingCard still offers the default
   * and the entry in use.
   */
  corporateTemplates: (kind: CorporateTemplateKind, includeRetired = false) =>
    request<{ templates: CorporateTemplate[] }>(
      `/corporate-templates?kind=${encodeURIComponent(kind)}` + (includeRetired ? '&includeRetired=1' : ''),
      {},
      { quiet: true },
    ),
  /**
   * The catalogue writes are all `quiet`: CorporateTemplatesPage states every refusal in place —
   * the upload's 413 `too_large` / 422 `not_pptx` | `macro_enabled` (as a bid upload), 409
   * `name_taken` | `builtin` | `is_default`, 422 `bad_name` | `bad_master` | `unknown_template`.
   */
  addCorporateTemplate: (kind: CorporateTemplateKind, name: string, base64: string) =>
    request<{ ok: true; id: string; trimmed?: TemplateTrim }>(
      '/corporate-templates',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, name, base64 }) },
      { quiet: true },
    ),
  /** Rename an uploaded entry and/or pick the slide master it renders on. */
  updateCorporateTemplate: (id: string, patch: { name?: string; master?: number }) =>
    request<{ ok: true }>(
      `/corporate-templates/${encodeURIComponent(id)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) },
      { quiet: true },
    ),
  /** Make an active entry the corporate default. */
  setCorporateTemplateDefault: (kind: CorporateTemplateKind, id: string) =>
    request<{ ok: true }>(
      '/corporate-templates/default',
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, id }) },
      { quiet: true },
    ),
  /** Retire an uploaded entry (never deleted; bids that picked it keep rendering on it). */
  retireCorporateTemplate: (id: string) =>
    request<{ ok: true }>(`/corporate-templates/${encodeURIComponent(id)}`, { method: 'DELETE' }, { quiet: true }),
  // ─── Bid Office templates (a bid's PowerPoint and Word templates, AB#441) ───
  /**
   * A bid's templates of one kind (any member; reading it creates the library from what the bid
   * used before). `quiet`: the card says a failure in place. The writes below are `quiet` too —
   * every refusal (409 `library_full` | `duplicate` | `name_taken` | `template_changed` | `is_default`
   * | `in_use { usedBy }`, 413 `too_large`, 422 `not_16_9` | `not_pptx` | `not_word` | `macro_enabled`
   * | `no_content_marker` | …) is worded by lib/office-templates.ts#officeRefusalText.
   */
  officeTemplates: (bid: string, kind: OfficeTemplateKind) =>
    request<{ templates: OfficeTemplate[] }>(
      `/bids/${encodeURIComponent(bid)}/office-templates?kind=${encodeURIComponent(kind)}`,
      {},
      { quiet: true },
    ),
  /** Add a template: a snapshot of a corporate catalogue entry (on one of its masters), or an upload. */
  addOfficeTemplate: (
    bid: string,
    kind: OfficeTemplateKind,
    from: { catalogueId: string; master?: number } | { base64: string; name: string },
  ) =>
    request<{ template: OfficeTemplate; trimmed?: TemplateTrim }>(
      `/bids/${encodeURIComponent(bid)}/office-templates`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, from }) },
      { quiet: true },
    ),
  renameOfficeTemplate: (bid: string, id: string, name: string) =>
    request<{ template: OfficeTemplate }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) },
      { quiet: true },
    ),
  /** A new version from a file; `baseVersion` = the version it replaces (stale → 409 `template_changed`). */
  replaceOfficeTemplate: (bid: string, id: string, base64: string, baseVersion: number) =>
    request<{ template: OfficeTemplate; trimmed?: TemplateTrim }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/content`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base64, baseVersion }) },
      { quiet: true },
    ),
  setOfficeTemplateDefault: (bid: string, id: string) =>
    request<{ ok: true }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/default`,
      { method: 'POST' },
      { quiet: true },
    ),
  /** The kept versions, newest first. */
  officeTemplateVersions: (bid: string, id: string) =>
    request<{ current: number; versions: OfficeTemplateVersion[] }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/versions`,
      {},
      { quiet: true },
    ),
  /** A new version copied from `version`; `baseVersion` as for a replace. */
  revertOfficeTemplate: (bid: string, id: string, version: number, baseVersion: number) =>
    request<{ template: OfficeTemplate }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/versions/${version}/revert`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ baseVersion }) },
      { quiet: true },
    ),
  /**
   * A template's shared draft (AB#453; any member — opening it seeds it the first time): its room,
   * the settings as drafted and as published, their structure, the unpublished changes, `canEdit`.
   * The template view (AB#454) reads it before connecting to the room. `quiet`: the view says a
   * failure in place.
   */
  officeTemplateDraft: (bid: string, id: string) =>
    request<{ draft: TemplateDraftView }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/draft`,
      {},
      { quiet: true },
    ),
  /** Publish the draft as a new version (canEditBid); `changed: false` = nothing to publish. */
  publishOfficeTemplate: (bid: string, id: string) =>
    request<PublishResult>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/publish`,
      { method: 'POST' },
      { quiet: true },
    ),
  /** Drop the draft's unpublished changes (canEditBid): the room is re-seeded from the latest version. */
  discardOfficeTemplateDraft: (bid: string, id: string) =>
    request<{ discarded: number; draft: TemplateDraftView }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}/draft/discard`,
      { method: 'POST' },
      { quiet: true },
    ),
  /** Refused 409 `in_use { usedBy }` while artefacts name it, unless `replaceWith` names where they move. */
  deleteOfficeTemplate: (bid: string, id: string, replaceWith?: string) =>
    request<{ ok: true; moved: Array<{ artefactId: string; title: string }> }>(
      `/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(id)}` +
        (replaceWith ? `?replaceWith=${encodeURIComponent(replaceWith)}` : ''),
      { method: 'DELETE' },
      { quiet: true },
    ),
  /**
   * An artefact's own templates: a deck's default PowerPoint template (+ master), a prose
   * artefact's Word template. `null` = back to the bid default; an absent key is left as it is.
   */
  setArtefactOfficeTemplates: (
    bid: string,
    artefactId: string,
    patch: { slidesTemplate?: { templateId: string; master?: number } | null; wordTemplate?: string | null },
  ) =>
    request<{ slidesTemplate: ArtefactSlidesTemplate | null; wordTemplate: string | null }>(
      '/artefacts/office-templates',
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bid, artefactId, ...patch }) },
      { quiet: true },
    ),
  /** Per-bid AI token usage + cost, aggregated across ALL users. */
  bidCost: (bid: string) =>
    request<{
      currency: string;
      spendTotal: number;
      effortTotal: number;
      byModel: { model: string; category: string; tokens: number; cost: number }[];
      budget: { bidId: string; maxTokens: number; consumed: number };
      budgetExceeded: boolean;
    }>(`/bids/${encodeURIComponent(bid)}/cost`),
  // ─── Workflow (bid-factory DAG) ─────────────────────────────────────────
  /**
   * The bid's workflow, or `null` when none has been created yet — the backend answers that with
   * 200 + a `null` body. `allowNotFound` stays so an older backend's 404 still reads as `null`.
   */
  workflow: (bid: string) =>
    request<BidWorkflow | null>(`/workflow?bid=${encodeURIComponent(bid)}`, {}, { allowNotFound: true }),
  /**
   * The static skill catalog for a workflow template — no bid, no ACL. Powers the chat's
   * slash-menu before a workflow has been created for the bid (see ChatPanel's `noWorkflowYet`).
   */
  workflowCatalog: (workflowType?: WorkflowType) =>
    request<{ nodes: WorkflowCatalogNode[] }>(
      `/workflow/catalog${workflowType ? `?workflowType=${encodeURIComponent(workflowType)}` : ''}`,
    ),
  createWorkflow: (bid: string, workflowType: WorkflowType, meta: WorkflowMeta) =>
    request<BidWorkflow>('/workflow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, workflowType, meta }),
    }),
  patchWorkflowNode: (
    bid: string,
    nodeId: string,
    patch: { statut?: NodeStatut; owner?: string; enabled?: boolean; notes?: string; statusGlobal?: StatusGlobal },
  ) =>
    request<BidWorkflow>('/workflow/node', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bid, nodeId, ...patch }),
    }),
  workingDocs: (bid: string) =>
    request<{ nodes: WorkingDocNode[] }>(`/workflow/working-docs?bid=${encodeURIComponent(bid)}`),

  // ─── Passerelle /agent/* (workflow generation) ────────────────────────────

  /** Start a generate-node job: local agent writes artifact + review, marks node en_cours. */
  generateNode: (bid: string, nodeId: string) =>
    agentRequest<{ jobId: string }>('/agent/generate-node', { bid, nodeId }),

  /**
   * Run a skill agentically: the tool-enabled agent writes to the appropriate targets.
   * - `contextRoom` (the currently open artefact) → primary artifact target.
   * - `instruction` free text → may name other artefact targets or qualify the output.
   * - No contextRoom + no instruction → same as generate-node (travail + revue).
   * - `kb` carries the chat's KB toggles; an omitted flag lets the skill's own default apply.
   */
  runSkill: (bid: string, nodeId: string, instruction: string, contextRoom: string | null, scope: ChatScope, agentName?: string, model?: string, kb?: SkillKb) =>
    agentRequest<{ jobId: string }>('/agent/run-skill', { bid, nodeId, instruction, contextRoom, scope, agent: agentName, model, ...kb }),

  /**
   * Generate a Word (.docx) or Excel (.xlsx) export via the local agent.
   * The agent uses generate_word_export / generate_excel_export tools.
   * Returns a jobId (kind='chat') — poll agentJob(id) for the download URL in the message.
   */
  runExport: (bid: string, format: 'word' | 'excel', room: string | null, scope: ChatScope, agentName?: string, model?: string) =>
    agentRequest<{ jobId: string }>('/agent/run-export', { bid, format, room, scope, agent: agentName, model }),

  /** Seed the bid's RAG with factory/knowledge files from the Solar knowledge library. */
  seedKnowledge: (bid: string) =>
    agentRequest<{ jobId: string }>('/agent/seed-knowledge', { bid }),

  // ─── User skills ─────────────────────────────────────────────────────────

  /** List available user skills (reads the passerelle's user-skills/ directory). */
  userSkills: () =>
    agentGet<{ skills: UserSkill[] }>('/agent/user-skills'),

  /**
   * Run a user skill agentically. User skills do not require an initialised workflow.
   * The agent uses create_part / write_part to build the artefact.
   */
  runUserSkill: (bid: string, skillId: string, instruction: string, contextRoom: string | null, scope: ChatScope, agentName?: string, model?: string, kb?: SkillKb) =>
    agentRequest<{ jobId: string }>('/agent/run-user-skill', { bid, skillId, instruction, contextRoom, scope, agent: agentName, model, ...kb }),
};
